import React, { useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, InstagramIcon, LinkedinIcon, MailIcon, PlusIcon, CheckIcon, MessageSquareIcon, GitBranchIcon, UsersIcon, LightbulbIcon, WrenchIcon, SparklesIcon, ZapIcon, PhoneIcon, MessageCircleIcon, MapPinIcon, ClockIcon, ChevronDownIcon, SearchIcon, StarIcon, SendIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
/* ------------------------------------------------------------------ */
/*  Shared mockup primitives                                           */
/* ------------------------------------------------------------------ */
function BrowserFrame({
  url,
  children
}: {
  url: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-md overflow-hidden border border-[var(--divider)] shadow-lg bg-white" data-id="element-2285">
      <div className="bg-[#F4F5F7] border-b border-[var(--divider)] px-3 py-2 flex items-center gap-3" data-id="element-2286">
        <div className="flex gap-1.5" data-id="element-2287">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" data-id="element-2288" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" data-id="element-2289" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" data-id="element-2290" />
        </div>
        <div className="flex-1 bg-white rounded-sm border border-[var(--divider)] px-2.5 py-0.5 text-[10px] font-mono text-[var(--text-muted)] truncate" data-id="element-2291">
          {url}
        </div>
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-2292">
          v2026.1
        </div>
      </div>
      <div className="bg-white" data-id="element-2293">{children}</div>
    </div>;
}
function SiteNav({
  active
}: {
  active: 'inicio' | 'servicios' | 'packs' | 'ai' | 'nosotros' | 'terms' | 'contacto' | 'portfolio' | 'solicitar';
}) {
  const items: Array<{
    key: typeof active;
    label: string;
  }> = [{
    key: 'inicio',
    label: 'Inicio'
  }, {
    key: 'servicios',
    label: 'Servicios'
  }, {
    key: 'packs',
    label: 'Packs'
  }, {
    key: 'ai',
    label: 'AI'
  }, {
    key: 'portfolio',
    label: 'Portfolio'
  }, {
    key: 'nosotros',
    label: 'Nosotros'
  }];
  return <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--divider)]" data-id="element-2294">
      <div className="flex items-center gap-6" data-id="element-2295">
        <img src={METRO_LOGO_URL} alt="me—tro" className="h-5 w-auto" data-id="element-2296" />
        <nav className="flex items-center gap-5 text-[10px]" data-id="element-2297">
          {items.map(i => <a key={i.key} className={i.key === active ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]'} data-id="element-2298">
              {i.label}
            </a>)}
          <a className={active === 'contacto' ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)] hover:text-[var(--text-ink)]'} data-id="element-2299">
            Contacto
          </a>
        </nav>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-2300">
        <span data-id="element-2301">ES / EN</span>
        <button className="px-3 py-1.5 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium" data-id="element-2302">
          Hablemos
        </button>
      </div>
    </div>;
}
function SiteFooter() {
  return <div className="bg-[var(--text-ink)] text-white px-6 py-8 mt-0" data-id="element-2303">
      <div className="grid grid-cols-12 gap-4 mb-6" data-id="element-2304">
        <div className="col-span-6" data-id="element-2305">
          <div className="font-space text-2xl font-bold tracking-tight leading-tight" data-id="element-2306">
            Donde las
            <br data-id="element-2307" />
            ideas conectan.
          </div>
          <div className="text-[10px] opacity-60 mt-2" data-id="element-2308">
            Metro Agency · Barcelona
          </div>
        </div>
        <div className="col-span-2" data-id="element-2309">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-2310">
            EXPLORA
          </div>
          <div className="space-y-1 text-[10px]" data-id="element-2311">
            <div data-id="element-2312">Inicio</div>
            <div data-id="element-2313">Servicios</div>
            <div data-id="element-2314">Packs</div>
            <div data-id="element-2315">AI</div>
            <div data-id="element-2316">Nosotros</div>
            <div data-id="element-2317">Contacto</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-2318">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-2319">
            ESTUDIO
          </div>
          <div className="space-y-1 text-[10px] opacity-80" data-id="element-2320">
            <div data-id="element-2321">Carrer de la Marina, 145</div>
            <div data-id="element-2322">08013 Barcelona</div>
            <div data-id="element-2323">hello@metroagency.co</div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-2324">
          <div className="font-mono text-[8px] opacity-50 tracking-widest mb-2" data-id="element-2325">
            SOCIAL
          </div>
          <div className="flex gap-2 mt-1" data-id="element-2326">
            <InstagramIcon className="w-3.5 h-3.5" data-id="element-2327" />
            <LinkedinIcon className="w-3.5 h-3.5" data-id="element-2328" />
            <MailIcon className="w-3.5 h-3.5" data-id="element-2329" />
          </div>
        </div>
      </div>
      <div className="border-t border-white/20 pt-3 flex items-center justify-between text-[9px] opacity-60" data-id="element-2330">
        <span data-id="element-2331">© 2026 Metro Agency S.L. · NIF B12345678</span>
        <span data-id="element-2332">Política de privacidad · Aviso legal · Cookies</span>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  Service network mark — small reusable graphic                      */
/* ------------------------------------------------------------------ */
function ServiceNetwork({
  height = 200
}: {
  height?: number;
}) {
  return <svg viewBox="0 0 600 200" className="w-full" style={{
    height
  }} data-id="element-2333">
      <line x1="20" y1="60" x2="580" y2="60" stroke="#1A2BFF" strokeWidth="3" data-id="element-2334" />
      <line x1="20" y1="100" x2="580" y2="100" stroke="#E8C547" strokeWidth="3" data-id="element-2335" />
      <line x1="20" y1="140" x2="580" y2="140" stroke="#F26A35" strokeWidth="3" data-id="element-2336" />
      <line x1="20" y1="180" x2="580" y2="180" stroke="#2A8E47" strokeWidth="3" data-id="element-2337" />
      {[60, 100, 140, 180].map((y, i) => [120, 240, 360, 480].map((x, j) => <circle key={`${i}-${j}`} cx={x} cy={y} r="6" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="2" data-id="element-2338" />))}
      <text x="20" y="30" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#6B6E78" letterSpacing="1.5" data-id="element-2339">
        STRATEGY · CREATIVE · PRODUCTION · PERFORMANCE
      </text>
    </svg>;
}
/* ================================================================== */
/*  HOME PAGE MOCKUP                                                   */
/* ================================================================== */
function HomePageMockup() {
  return <BrowserFrame url="metroagency.co" data-id="element-2340">
      <SiteNav active="inicio" data-id="element-2341" />

      {/* Hero — full bleed cool grey */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-2342">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2343">
          AGENCIA CREATIVA · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-2344">
          Donde las
          <br data-id="element-2345" />
          ideas conectan.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2346">
          Conectamos estrategia, creatividad y producción en una sola línea —
          para que las campañas lleguen donde tienen que llegar, sin perderse
          entre proveedores.
        </div>
        <div className="flex items-center gap-3 mt-6" data-id="element-2347">
          <button className="px-4 py-2 bg-[var(--text-ink)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2348">
            Build something that moves people
            <ArrowRightIcon className="w-3 h-3" data-id="element-2349" />
          </button>
          <button className="px-4 py-2 border border-[var(--text-ink)] rounded-full text-xs font-medium" data-id="element-2350">
            Ver trabajos
          </button>
        </div>
        <div className="mt-10" data-id="element-2351">
          <ServiceNetwork height={180} data-id="element-2352" />
        </div>
      </div>

      {/* Service network explainer */}
      <div className="px-6 py-10 bg-white" data-id="element-2353">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2354">
          01 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight max-w-lg mb-6" data-id="element-2355">
          Cuatro líneas. Una red. Cero traspasos.
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-2356">
          {[{
          n: '01',
          t: 'Estrategia',
          d: 'Posicionamiento, audiencias, mensaje. Donde decidimos qué construir y por qué.'
        }, {
          n: '02',
          t: 'Creatividad',
          d: 'Identidad, dirección de arte, narrativa. La idea con la que el resto se conecta.'
        }, {
          n: '03',
          t: 'Producción',
          d: 'Vídeo, fotografía, web, eventos. Donde la idea se vuelve real.'
        }, {
          n: '04',
          t: 'Performance',
          d: 'Paid media, analítica, optimización. Donde medimos si la conexión funcionó.'
        }].map(c => <div key={c.n} className="border border-[var(--divider)] p-4 rounded-sm" data-id="element-2357">
              <div className="font-mono text-[10px] text-[var(--accent-blue)] font-bold mb-2" data-id="element-2358">
                {c.n}
              </div>
              <div className="font-space font-bold text-base mb-1" data-id="element-2359">{c.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-2360">
                {c.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* Manifesto callout */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-2361">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-2362">
          MANIFIESTO
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-2363">
          No creemos en magia. Creemos en sistemas que funcionan. Construimos
          infraestructura para ideas que duran — campañas que conectan
          estrategia con ejecución, marcas que mueven personas.
        </div>
        <div className="mt-5 text-[10px] opacity-70 font-mono tracking-widest" data-id="element-2364">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Selected work */}
      <div className="px-6 py-10 bg-white" data-id="element-2365">
        <div className="flex items-end justify-between mb-5" data-id="element-2366">
          <div data-id="element-2367">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2368">
              02 · TRABAJO SELECCIONADO
            </div>
            <div className="font-space font-bold text-2xl tracking-tight" data-id="element-2369">
              Lo último de la línea.
            </div>
          </div>
          <a className="text-[10px] font-medium underline" data-id="element-2370">Ver todo →</a>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-2371">
          {[{
          c: '#1A2BFF',
          t: 'Cliente · Sector',
          n: 'Campaña 360'
        }, {
          c: '#0E0E12',
          t: 'Cliente · Sector',
          n: 'Identidad'
        }, {
          c: '#E8C547',
          t: 'Cliente · Sector',
          n: 'Producción'
        }].map((w, i) => <div key={i} data-id="element-2372">
              <div className="aspect-[4/5] rounded-sm flex items-end p-3" style={{
            background: w.c
          }} data-id="element-2373">
                <div className={'text-[10px] font-bold ' + (w.c === '#E8C547' ? 'text-[var(--text-ink)]' : 'text-white')} data-id="element-2374">
                  {w.n}
                </div>
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-1.5 font-mono tracking-wider" data-id="element-2375">
                {w.t}
              </div>
            </div>)}
        </div>
      </div>

      {/* Clients strip */}
      <div className="px-6 py-6" style={{
      background: '#EDEEF1'
    }} data-id="element-2376">
        <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-3 text-center" data-id="element-2377">
          CONFÍAN EN NOSOTROS
        </div>
        <div className="flex items-center justify-around opacity-50" data-id="element-2378">
          {['CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE', 'CLIENTE'].map((c, i) => <div key={i} className="text-[10px] font-bold tracking-wider" data-id="element-2379">
                {c}
              </div>)}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-2380">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-2381">
          ¿Una idea que conectar?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-2382">
          Cuéntanos el proyecto, el contexto y el plazo. Respondemos en menos de
          un día laborable.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2383">
          Start the conversation
          <ArrowRightIcon className="w-3 h-3" data-id="element-2384" />
        </button>
      </div>

      <SiteFooter data-id="element-2385" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  SERVICES PAGE MOCKUP                                               */
/* ================================================================== */
function ServicesPageMockup() {
  return <BrowserFrame url="metroagency.co/servicios" data-id="element-2386">
      <SiteNav active="servicios" data-id="element-2387" />

      {/* Hero — full bleed blue */}
      <div className="px-6 py-12 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-2388">
        <div className="font-mono text-[9px] opacity-75 tracking-widest mb-3" data-id="element-2389">
          SERVICIOS
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-2390">
          Una línea.
          <br data-id="element-2391" />
          Cuatro estaciones.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2392">
          Estrategia, creatividad, producción y performance — integradas de
          principio a fin. Sin externalizar las partes críticas. Sin perder la
          coherencia entre fases.
        </div>
        <div className="mt-8 grid grid-cols-4 gap-2 max-w-2xl" data-id="element-2393">
          {['Estrategia', 'Creatividad', 'Producción', 'Performance'].map((s, i) => <div key={i} className="text-center text-[9px] py-2 rounded-sm border border-white/30" data-id="element-2394">
                <div className="font-mono opacity-70" data-id="element-2395">0{i + 1}</div>
                <div className="font-bold mt-0.5" data-id="element-2396">{s}</div>
              </div>)}
        </div>
      </div>

      {/* Service blocks */}
      <div className="px-6 py-10 bg-white space-y-4" data-id="element-2397">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2398">
          01 · QUÉ HACEMOS
        </div>

        {[{
        cat: 'CREATIVIDAD',
        num: '01',
        title: 'Identidad, dirección de arte y narrativa',
        desc: 'Construimos sistemas de marca que viajan con todo lo demás. No logos sueltos — sistemas que aguantan el uso real.',
        items: ['Identidad visual y verbal', 'Dirección de arte para campaña', 'Sistemas de diseño', 'Brand guidelines y handover']
      }, {
        cat: 'MARKETING & PERFORMANCE',
        num: '02',
        title: 'Estrategia, datos y optimización',
        desc: 'Estrategia con cabeza de negocio. Paid media, analítica y optimización con foco en el resultado, no en el dashboard.',
        items: ['Estrategia de marca y go-to-market', 'Paid social y search', 'Analítica y atribución', 'CRO y optimización continua']
      }, {
        cat: 'PRODUCCIÓN & EVENTOS',
        num: '03',
        title: 'Vídeo, fotografía, web y experiencias',
        desc: 'Producción in-house de principio a fin. Lo que diseñamos lo producimos — sin traspasos, sin teléfono escacharrado.',
        items: ['Vídeo y fotografía', 'Diseño y desarrollo web', 'Eventos y activaciones', 'Contenido para social y paid']
      }].map(s => <div key={s.num} className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-2399">
            <div className="flex items-start gap-4 p-5 border-b border-[var(--divider)]" data-id="element-2400">
              <div className="flex-shrink-0" data-id="element-2401">
                <div className="font-mono text-[10px] text-[var(--accent-blue)] tracking-widest mb-1 font-bold" data-id="element-2402">
                  {s.cat}
                </div>
                <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none" data-id="element-2403">
                  {s.num}
                </div>
              </div>
              <div className="flex-1" data-id="element-2404">
                <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-2405">
                  {s.title}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-2406">
                  {s.desc}
                </div>
              </div>
              <MessageSquareIcon className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" strokeWidth={1.5} data-id="element-2407" />
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 px-5 py-3 bg-[var(--bg-canvas)]" data-id="element-2408">
              {s.items.map(it => <div key={it} className="flex items-start gap-2 text-[10px]" data-id="element-2409">
                  <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-2410" />
                  <span data-id="element-2411">{it}</span>
                </div>)}
            </div>
          </div>)}
      </div>

      {/* Approach callout */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2412">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2413">
          02 · CÓMO TRABAJAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-2414">
          Hands-on. End-to-end.
          <br data-id="element-2415" />
          Sin agencias intermedias.
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-2416">
          {[{
          t: 'Un solo equipo',
          d: 'El mismo equipo que diseña es el que produce. Coherencia por construcción.'
        }, {
          t: 'Un solo punto de contacto',
          d: 'Hablas con quien decide. No con quien gestiona al que decide.'
        }, {
          t: 'Un solo lenguaje',
          d: 'Lo estratégico, lo creativo y lo técnico hablan el mismo idioma desde el primer día.'
        }].map(b => <div key={b.t} className="bg-white p-4 rounded-sm border border-[var(--divider)]" data-id="element-2417">
              <div className="font-space font-bold text-sm mb-1.5" data-id="element-2418">{b.t}</div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-2419">
                {b.d}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12 bg-white" data-id="element-2420">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4" data-id="element-2421">
          ¿Planificando una campaña?
        </div>
        <div className="text-sm text-[var(--text-muted)] mb-5 max-w-md" data-id="element-2422">
          Mejor empezar por el mapa. Cuéntanos el objetivo y dibujamos la línea
          contigo.
        </div>
        <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2423">
          Map your next campaign
          <ArrowRightIcon className="w-3 h-3" data-id="element-2424" />
        </button>
      </div>

      <SiteFooter data-id="element-2425" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  ABOUT PAGE MOCKUP                                                  */
/* ================================================================== */
function AboutPageMockup() {
  return <BrowserFrame url="metroagency.co/nosotros" data-id="element-2426">
      <SiteNav active="nosotros" data-id="element-2427" />

      {/* Hero */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-2428">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2429">
          NOSOTROS · BARCELONA
        </div>
        <div className="font-space font-bold text-5xl leading-[0.92] tracking-tight mb-4" data-id="element-2430">
          Construimos
          <br data-id="element-2431" />
          la infraestructura.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2432">
          Metro nace en Barcelona, de la dificultad de encontrar una agencia
          digital que ejecute la campaña completa sin externalizar las partes
          críticas. Construimos lo que nos hubiera gustado contratar.
        </div>
      </div>

      {/* Dark manifesto */}
      <div className="px-6 py-12 text-white" style={{
      background: '#0E0E12'
    }} data-id="element-2433">
        <div className="font-mono text-[9px] tracking-widest mb-3 text-[var(--accent-blue)]" data-id="element-2434">
          MANIFIESTO · 80 PALABRAS
        </div>
        <div className="font-space text-xl leading-snug tracking-tight font-medium max-w-2xl" data-id="element-2435">
          <p className="mb-3 font-bold" data-id="element-2436">
            No creemos en magia. Creemos en sistemas que funcionan.
          </p>
          <p className="mb-3 text-base opacity-90 font-normal" data-id="element-2437">
            En un mundo saturado de contenido, construimos infraestructura para
            ideas que duran. Campañas que conectan estrategia con ejecución,
            marcas que mueven personas, historias que merecen la atención.
          </p>
          <p className="text-base opacity-90 font-normal" data-id="element-2438">
            No perseguimos trends. Construimos líneas entre lo que necesitas
            comunicar y quién necesita escucharlo.
          </p>
        </div>
        <div className="mt-6 pt-4 border-t border-white/20 font-mono text-[10px] tracking-widest opacity-70" data-id="element-2439">
          TUS IDEAS · NUESTRAS VÍAS · MOVAMOS
        </div>
      </div>

      {/* Voice */}
      <div className="px-6 py-10 bg-white" data-id="element-2440">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2441">
          01 · CÓMO HABLAMOS
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-2442">
          Punto. No exclamación.
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-2443">
          <div className="p-4 rounded-sm text-white" style={{
          background: '#1A2BFF'
        }} data-id="element-2444">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-2445">
              USAMOS
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-2446">
              Conectar · construir · red · línea · estrategia · campaña · real ·
              honesto · directo · intencional
            </div>
          </div>
          <div className="p-4 rounded-sm bg-[var(--text-ink)] text-white" data-id="element-2447">
            <div className="font-mono text-[9px] opacity-60 tracking-widest mb-2" data-id="element-2448">
              NUNCA
            </div>
            <div className="text-[10px] leading-relaxed" data-id="element-2449">
              Magia · wow factor · rock · synergy · disrupt · journey ·
              game-changer · guru · ninja
            </div>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2450">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2451">
          02 · EQUIPO
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-5 max-w-lg" data-id="element-2452">
          Las personas conducen el tren.
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-2453">
          {[['Nombre Apellido', 'Strategy & accounts'], ['Nombre Apellido', 'Creative director'], ['Nombre Apellido', 'Production lead'], ['Nombre Apellido', 'Performance & data']].map(([n, r], i) => <div key={i} data-id="element-2454">
              <div className="aspect-square rounded-sm mb-2" style={{
            background: i % 2 === 0 ? '#1A2BFF' : '#0E0E12'
          }} data-id="element-2455" />
              <div className="font-space font-bold text-[11px] leading-tight" data-id="element-2456">
                {n}
              </div>
              <div className="text-[9px] text-[var(--text-muted)] mt-0.5" data-id="element-2457">
                {r}
              </div>
            </div>)}
        </div>
      </div>

      {/* Numbers */}
      <div className="px-6 py-10 bg-white" data-id="element-2458">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-5" data-id="element-2459">
          03 · ESTACIONES POR LAS QUE HEMOS PASADO
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-2460">
          {[['+50', 'Campañas integradas'], ['+30', 'Marcas con identidad nueva'], ['12', 'Sectores cubiertos'], ['4', 'Idiomas operativos']].map(([n, l]) => <div key={l} className="border-t border-[var(--text-ink)] pt-3" data-id="element-2461">
              <div className="font-space font-bold text-3xl text-[var(--accent-blue)] leading-none mb-1" data-id="element-2462">
                {n}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-tight" data-id="element-2463">
                {l}
              </div>
            </div>)}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 py-12" style={{
      background: '#1A2BFF'
    }} data-id="element-2464">
        <div className="font-space font-bold text-3xl leading-[0.95] tracking-tight max-w-xl mb-4 text-white" data-id="element-2465">
          Empezamos por una conversación.
        </div>
        <div className="text-sm text-white/80 mb-5 max-w-md" data-id="element-2466">
          Sin briefing pre-rellenado. Sin formulario de 12 campos. Solo
          cuéntanos el contexto.
        </div>
        <button className="px-5 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2467">
          Let's connect the dots
          <ArrowRightIcon className="w-3 h-3" data-id="element-2468" />
        </button>
      </div>

      <SiteFooter data-id="element-2469" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  PACKS PAGE MOCKUP                                                  */
/* ================================================================== */
function PriceTag({
  amount,
  highlight = false,
  small = false
}: {
  amount: string;
  highlight?: boolean;
  small?: boolean;
}) {
  return <div className={'inline-flex items-baseline gap-1 ' + (highlight ? 'text-[var(--accent-blue)]' : '')} data-id="element-2470">
      <span className={'font-space font-bold tracking-tight leading-none ' + (small ? 'text-xl' : 'text-3xl')} data-id="element-2471">
        {amount}
      </span>
      <span className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider" data-id="element-2472">
        IVA INCL.
      </span>
    </div>;
}
function PackCard({
  tier,
  tierLabel,
  name,
  tagline,
  price,
  timeline,
  ideal,
  features,
  warnings,
  cta,
  accent,
  savings,
  combo
}: {
  tier: string;
  tierLabel: string;
  name: string;
  tagline: string;
  price: string;
  timeline: string;
  ideal: string;
  features: string[];
  warnings?: string[];
  cta: string;
  accent: 'blue' | 'ink' | 'grey';
  savings?: string;
  combo?: boolean;
}) {
  const bg = accent === 'blue' ? '#1A2BFF' : accent === 'ink' ? '#0E0E12' : '#EDEEF1';
  const fg = accent === 'grey' ? '#0E0E12' : '#FFFFFF';
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-2473">
      {/* Header */}
      <div className="p-5" style={{
      background: bg,
      color: fg
    }} data-id="element-2474">
        <div className="flex items-center justify-between mb-3" data-id="element-2475">
          <div className="font-mono text-[9px] tracking-widest opacity-75" data-id="element-2476">
            {tier} · {tierLabel}
          </div>
          {combo && <div className="text-[8px] font-bold tracking-widest bg-white/15 px-2 py-0.5 rounded-full" data-id="element-2477">
              COMBO
            </div>}
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-1" data-id="element-2478">
          {name}
        </div>
        <div className="text-[11px] opacity-85 leading-relaxed mb-4" data-id="element-2479">
          {tagline}
        </div>

        <div className="flex items-baseline gap-2" data-id="element-2480">
          <span className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-2481">
            {price}
          </span>
          <span className="text-[9px] font-mono tracking-wider opacity-75" data-id="element-2482">
            IVA INCL.
          </span>
        </div>
        {savings && <div className="text-[10px] mt-1 opacity-90 font-medium" data-id="element-2483">
            {savings}
          </div>}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col" data-id="element-2484">
        <div className="grid grid-cols-2 gap-3 pb-3 border-b border-[var(--divider)] mb-3 text-[10px]" data-id="element-2485">
          <div data-id="element-2486">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2487">
              PLAZO
            </div>
            <div className="font-medium" data-id="element-2488">{timeline}</div>
          </div>
          <div data-id="element-2489">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2490">
              IDEAL PARA
            </div>
            <div className="font-medium leading-snug" data-id="element-2491">{ideal}</div>
          </div>
        </div>

        {warnings && warnings.length > 0 && <div className="mb-3" data-id="element-2492">
            <div className="font-mono text-[8px] text-[var(--accent-orange)] tracking-widest mb-2 font-bold" data-id="element-2493">
              LIMITACIONES EXPLÍCITAS
            </div>
            <ul className="space-y-1.5" data-id="element-2494">
              {warnings.map((w, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug text-[var(--text-muted)]" data-id="element-2495">
                  <span className="text-[var(--accent-orange)] flex-shrink-0" data-id="element-2496">
                    ⚠
                  </span>
                  <span data-id="element-2497">{w}</span>
                </li>)}
            </ul>
          </div>}

        <div data-id="element-2498">
          <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2499">
            INCLUYE
          </div>
          <ul className="space-y-1.5" data-id="element-2500">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug" data-id="element-2501">
                <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-2502" />
                <span data-id="element-2503">{f}</span>
              </li>)}
          </ul>
        </div>

        <div className="mt-5 pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-3" data-id="element-2504">
          <button className="flex-1 px-3 py-2 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-2505">
            {cta}
            <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-2506" />
          </button>
          <button className="px-3 py-2 border border-[var(--text-ink)] rounded-full text-[10px] font-medium" data-id="element-2507">
            Demo
          </button>
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  AI SOLUTIONS PAGE MOCKUP                                           */
/* ================================================================== */
function AIPageMockup() {
  return <BrowserFrame url="metroagency.co/ai" data-id="element-2508">
      <SiteNav active="ai" data-id="element-2509" />

      {/* Hero — dark with subtle network */}
      <div className="px-6 py-12 relative overflow-hidden" style={{
      background: '#0E0E12'
    }} data-id="element-2510">
        <div className="relative z-10" data-id="element-2511">
          <div className="flex items-center gap-2 mb-4" data-id="element-2512">
            <SparklesIcon className="w-3.5 h-3.5 text-[var(--accent-blue)]" strokeWidth={1.75} data-id="element-2513" />
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest" data-id="element-2514">
              NUEVA LÍNEA · AI SOLUTIONS
            </div>
          </div>
          <div className="font-space font-bold text-5xl leading-[0.95] tracking-tight text-white mb-4" data-id="element-2515">
            Tu negocio,
            <br data-id="element-2516" />
            <span className="text-[var(--accent-blue)]" data-id="element-2517">
              respondiendo a las 2am.
            </span>
          </div>
          <div className="font-helvetica text-sm text-white/70 leading-relaxed max-w-xl mb-6" data-id="element-2518">
            Chatbots que reservan, agentes que cualifican leads, flujos que
            ejecutan solos. Cinco servicios de IA con precio claro — setup más
            mensualidad, sin permanencia. Para negocios que quieren dejar de
            atender el WhatsApp manualmente.
          </div>
          <div className="flex items-center gap-3" data-id="element-2519">
            <button className="px-4 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2520">
              Hacer la auditoría · €490
              <ArrowRightIcon className="w-3 h-3" data-id="element-2521" />
            </button>
            <button className="px-4 py-2.5 border border-white/30 text-white rounded-full text-xs font-medium" data-id="element-2522">
              Ver todos los servicios
            </button>
          </div>

          {/* Mini stats */}
          <div className="grid grid-cols-4 gap-4 mt-10 pt-6 border-t border-white/15" data-id="element-2523">
            {[['5', 'Servicios de IA'], ['€490', 'Punto de entrada'], ['24/7', 'Sin horario'], ['0', 'Permanencia']].map(([n, l]) => <div key={l} data-id="element-2524">
                <div className="font-space font-bold text-3xl text-white tracking-tight leading-none" data-id="element-2525">
                  {n}
                </div>
                <div className="font-mono text-[9px] text-white/50 tracking-widest mt-1" data-id="element-2526">
                  {l}
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  01 — CHATBOT AI                                             */}
      {/* ============================================================ */}
      <div className="px-6 py-10 bg-white" data-id="element-2527">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2528">
          <div data-id="element-2529">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1 flex items-center gap-2" data-id="element-2530">
              <MessageSquareIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-2531" />
              01 · CHATBOT AI
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2532">
              Atención automática.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2533">
              Un bot que responde como tú lo harías — pero a cualquier hora.
              Conectado a tu web, WhatsApp o redes.
            </div>
          </div>
          <div className="text-right" data-id="element-2534">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2535">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2536">
              €490
            </div>
          </div>
        </div>

        {/* Adapted for landscape grid with 3 columns */}
        <div className="grid grid-cols-3 gap-4 mt-8 mb-8" data-id="element-2537">
          {/* Service Family: Web */}
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)] flex flex-col justify-between" data-id="element-2538">
            <div data-id="element-2539">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2540">
                €390
              </div>
              <div className="font-space font-bold text-xl mb-2" data-id="element-2541">
                Web Express
              </div>
              <div className="text-[10px] text-[var(--text-muted)] mb-4" data-id="element-2542">
                Página sencilla en Squarespace, optimizada para web, adaptada y
                lista en 72h. Ideal para presencia rápida.
              </div>
            </div>
            <button className="mt-2 px-3 py-1 bg-[var(--accent-blue)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-2" data-id="element-2543">
              Quiero mi web ahora
              <ArrowRightIcon className="w-3 h-3" data-id="element-2544" />
            </button>
          </div>
          {/* Service Family: Redes */}
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)] flex flex-col justify-between" data-id="element-2545">
            <div data-id="element-2546">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2547">
                €450/mes
              </div>
              <div className="font-space font-bold text-xl mb-2" data-id="element-2548">
                Redes Sociales
              </div>
              <div className="text-[10px] text-[var(--text-muted)] mb-4" data-id="element-2549">
                Gestión completa, calendario, publicaciones, atención y
                reportes. Estrategia y contenidos adaptados.
              </div>
            </div>
            <button className="mt-2 px-3 py-1 bg-[var(--accent-blue)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-2" data-id="element-2550">
              Empezar Social
              <ArrowRightIcon className="w-3 h-3" data-id="element-2551" />
            </button>
          </div>
          {/* Service Family: Branding */}
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)] flex flex-col justify-between" data-id="element-2552">
            <div data-id="element-2553">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2554">
                €1.200
              </div>
              <div className="font-space font-bold text-xl mb-2" data-id="element-2555">Branding</div>
              <div className="text-[10px] text-[var(--text-muted)] mb-4" data-id="element-2556">
                Logo, paleta, tipografía y sistema de marca. Todo listo para
                impulsar tu identidad.
              </div>
            </div>
            <button className="mt-2 px-3 py-1 bg-[var(--accent-blue)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-2" data-id="element-2557">
              Crear marca
              <ArrowRightIcon className="w-3 h-3" data-id="element-2558" />
            </button>
          </div>
          {/* Service Family: Foto / Video */}
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)] flex flex-col justify-between" data-id="element-2559">
            <div data-id="element-2560">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2561">
                €800
              </div>
              <div className="font-space font-bold text-xl mb-2" data-id="element-2562">
                Foto / Vídeo
              </div>
              <div className="text-[10px] text-[var(--text-muted)] mb-4" data-id="element-2563">
                Producción en estudio o en local, para web, social y campañas.
                Creatividad y edición en casa.
              </div>
            </div>
            <button className="mt-2 px-3 py-1 bg-[var(--accent-blue)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-2" data-id="element-2564">
              Pedir contenido
              <ArrowRightIcon className="w-3 h-3" data-id="element-2565" />
            </button>
          </div>
          {/* Service Family: Eventos a medida */}
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)] flex flex-col justify-between" data-id="element-2566">
            <div data-id="element-2567">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-2568">
                €5.000+
              </div>
              <div className="font-space font-bold text-xl mb-2" data-id="element-2569">Eventos</div>
              <div className="text-[10px] text-[var(--text-muted)] mb-4" data-id="element-2570">
                Organización, producción y cobertura de eventos presenciales o
                virtuales, a medida.
              </div>
            </div>
            <button className="mt-2 px-3 py-1 bg-[var(--accent-blue)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-2" data-id="element-2571">
              Planificar evento
              <ArrowRightIcon className="w-3 h-3" data-id="element-2572" />
            </button>
          </div>
        </div>
        {/* Testimonials */}
        <div className="mt-10 grid grid-cols-3 gap-4" data-id="element-2573">
          {/* Testimonial 1 */}
          <div className="border border-[var(--divider)] p-4 bg-[var(--bg-surface)] flex flex-col rounded-sm" data-id="element-2574">
            <div className="flex items-center mb-2" data-id="element-2575">
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)] mr-1" data-id="element-2576" />
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)]" data-id="element-2577" />
            </div>
            <div className="font-space font-bold mb-2" data-id="element-2578">
              “Excelente servicio y atención personalizada, lograron una web que
              refleja nuestra marca a la perfección.”
            </div>
            <div className="text-[10px] text-[var(--text-muted)]" data-id="element-2579">
              — Cliente feliz
            </div>
          </div>
          {/* Testimonial 2 */}
          <div className="border border-[var(--divider)] p-4 bg-[var(--bg-surface)] flex flex-col rounded-sm" data-id="element-2580">
            <div className="flex items-center mb-2" data-id="element-2581">
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)] mr-1" data-id="element-2582" />
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)]" data-id="element-2583" />
            </div>
            <div className="font-space font-bold mb-2" data-id="element-2584">
              “Rápidos, creativos y muy atentos, convertieron nuestra idea en
              realidad en solo días.”
            </div>
            <div className="text-[10px] text-[var(--text-muted)]" data-id="element-2585">
              — Cliente satisfecho
            </div>
          </div>
          {/* Testimonial 3 */}
          <div className="border border-[var(--divider)] p-4 bg-[var(--bg-surface)] flex flex-col rounded-sm" data-id="element-2586">
            <div className="flex items-center mb-2" data-id="element-2587">
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)] mr-1" data-id="element-2588" />
              <StarIcon className="w-4 h-4 text-[var(--accent-blue)]" data-id="element-2589" />
            </div>
            <div className="font-space font-bold mb-2" data-id="element-2590">
              “Muy profesionales, resolutivos y con muy buen gusto en diseño y
              estrategia.”
            </div>
            <div className="text-[10px] text-[var(--text-muted)]" data-id="element-2591">
              — Cliente top
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>;
}
/* ================================================================== */
/*  PAGE WRAPPER                                                       */
/* ================================================================== */
function PageRedesignBlock({
  number,
  title,
  url,
  intro,
  notes,
  mockup
}: {
  number: string;
  title: string;
  url: string;
  intro: string;
  notes: {
    tag: string;
    title: string;
    body: string;
  }[];
  mockup: React.ReactNode;
}) {
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-id="element-2592">
      <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6" data-id="element-2593">
        <div data-id="element-2594">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-3" data-id="element-2595">
            {number}
          </div>
          <h3 className="font-space text-4xl font-bold tracking-tight leading-[0.95] mb-2" data-id="element-2596">
            {title}
          </h3>
          <div className="font-mono text-xs text-[var(--accent-blue)] tracking-wider" data-id="element-2597">
            {url}
          </div>
          <p className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed mt-4" data-id="element-2598">
            {intro}
          </p>
        </div>
        <div className="space-y-3" data-id="element-2599">
          <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest" data-id="element-2600">
            CAMBIOS CLAVE
          </div>
          {notes.map((n, i) => <div key={i} className="border-l-2 border-[var(--accent-blue)] pl-4 py-1" data-id="element-2601">
              <div className="font-mono text-[9px] text-[var(--accent-blue)] font-bold tracking-widest mb-1" data-id="element-2602">
                {n.tag}
              </div>
              <div className="font-space font-medium text-sm leading-tight mb-1" data-id="element-2603">
                {n.title}
              </div>
              <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed" data-id="element-2604">
                {n.body}
              </div>
            </div>)}
        </div>
      </div>
      <div className="lg:col-span-7" data-id="element-2605">{mockup}</div>
    </div>;
}
/* ================================================================== */
/*  PHONE FRAME                                                        */
/* ================================================================== */
function PhoneFrame({
  url,
  children
}: {
  url: string;
  children: React.ReactNode;
}) {
  return <div className="flex justify-center py-8 bg-[var(--bg-canvas)]" data-id="element-2606">
      <div className="rounded-[2.5rem] p-2 shadow-2xl" style={{
      background: '#0E0E12',
      width: '320px'
    }} data-id="element-2607">
        <div className="rounded-[2rem] overflow-hidden bg-white" data-id="element-2608">
          <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[9px] font-mono" data-id="element-2609">
            <span className="font-bold" data-id="element-2610">9:41</span>
            <div className="flex items-center gap-1" data-id="element-2611">
              <span data-id="element-2612">5G</span>
              <span data-id="element-2613">100%</span>
            </div>
          </div>
          <div className="px-3 pb-2 border-b border-[var(--divider)]" data-id="element-2614">
            <div className="bg-[#F4F5F7] rounded-full px-3 py-1 text-[9px] font-mono text-[var(--text-muted)] truncate flex items-center gap-1.5" data-id="element-2615">
              <span className="text-[var(--accent-blue)]" data-id="element-2616">●</span>
              {url}
            </div>
          </div>
          <div className="bg-white max-h-[700px] overflow-hidden" data-id="element-2617">
            {children}
          </div>
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  VIEWPORT TOGGLE                                                    */
/* ================================================================== */
function ViewportToggle({
  view,
  onChange
}: {
  view: 'desktop' | 'mobile';
  onChange: (v: 'desktop' | 'mobile') => void;
}) {
  return <div className="flex items-center justify-between mb-3" data-id="element-2618">
      <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest" data-id="element-2619">
        VIEWPORT
      </div>
      <div className="relative inline-flex items-center bg-[var(--bg-canvas)] border border-[var(--divider)] rounded-full p-0.5" data-id="element-2620">
        <motion.div layout transition={{
        type: 'spring',
        stiffness: 500,
        damping: 35
      }} className="absolute top-0.5 bottom-0.5 bg-[var(--text-ink)] rounded-full" style={{
        width: 'calc(50% - 2px)',
        left: view === 'desktop' ? '2px' : 'calc(50%)'
      }} data-id="element-2621" />
        <button onClick={() => onChange('desktop')} className={'relative z-10 px-3 py-1 text-[10px] font-medium ' + (view === 'desktop' ? 'text-white' : 'text-[var(--text-muted)]')} data-id="element-2622">
          Desktop
        </button>
        <button onClick={() => onChange('mobile')} className={'relative z-10 px-3 py-1 text-[10px] font-medium ' + (view === 'mobile' ? 'text-white' : 'text-[var(--text-muted)]')} data-id="element-2623">
          Mobile
        </button>
      </div>
    </div>;
}
/* ================================================================== */
/*  AI PACK CARD                                                       */
/* ================================================================== */
function AIPackCard({
  tier,
  tierLabel,
  name,
  tagline,
  setup,
  monthly,
  range,
  timeline,
  ideal,
  features,
  cta,
  accent,
  icon: Icon,
  flagship
}: {
  tier: string;
  tierLabel: string;
  name: string;
  tagline: string;
  setup?: string;
  monthly?: string;
  range?: string;
  timeline: string;
  ideal: string;
  features: string[];
  cta: string;
  accent: 'blue' | 'ink' | 'grey';
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  flagship?: boolean;
}) {
  const bg = accent === 'blue' ? '#1A2BFF' : accent === 'ink' ? '#0E0E12' : '#EDEEF1';
  const fg = accent === 'grey' ? '#0E0E12' : '#FFFFFF';
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-2624">
      <div className="p-5" style={{
      background: bg,
      color: fg
    }} data-id="element-2625">
        <div className="flex items-center justify-between mb-3" data-id="element-2626">
          <div className="flex items-center gap-2" data-id="element-2627">
            <Icon className="w-3.5 h-3.5 opacity-90" strokeWidth={1.75} data-id="element-2628" />
            <div className="font-mono text-[9px] tracking-widest opacity-75" data-id="element-2629">
              {tier} · {tierLabel}
            </div>
          </div>
          {flagship && <div className="text-[8px] font-bold tracking-widest bg-white/15 px-2 py-0.5 rounded-full" data-id="element-2630">
              FLAGSHIP
            </div>}
        </div>
        <div className="font-space font-bold text-2xl leading-tight tracking-tight mb-1" data-id="element-2631">
          {name}
        </div>
        <div className="text-[11px] opacity-85 leading-relaxed mb-4" data-id="element-2632">
          {tagline}
        </div>
        {range ? <div data-id="element-2633">
            <div className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-2634">
              {range}
            </div>
            <div className="text-[9px] font-mono tracking-wider opacity-75 mt-1" data-id="element-2635">
              PROYECTO + MENSUAL · IVA INCL.
            </div>
          </div> : <div className="grid grid-cols-2 gap-3" data-id="element-2636">
            <div className="border-r border-white/20 pr-3" data-id="element-2637">
              <div className="font-mono text-[8px] tracking-widest opacity-70 mb-1" data-id="element-2638">
                SETUP
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-none" data-id="element-2639">
                {setup}
              </div>
            </div>
            <div data-id="element-2640">
              <div className="font-mono text-[8px] tracking-widest opacity-70 mb-1" data-id="element-2641">
                MENSUAL
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-none" data-id="element-2642">
                {monthly || '—'}
              </div>
            </div>
          </div>}
      </div>
      <div className="p-5 flex-1 flex flex-col" data-id="element-2643">
        <div className="grid grid-cols-2 gap-3 pb-3 border-b border-[var(--divider)] mb-3 text-[10px]" data-id="element-2644">
          <div data-id="element-2645">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2646">
              PLAZO
            </div>
            <div className="font-medium" data-id="element-2647">{timeline}</div>
          </div>
          <div data-id="element-2648">
            <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2649">
              IDEAL PARA
            </div>
            <div className="font-medium leading-snug" data-id="element-2650">{ideal}</div>
          </div>
        </div>
        <div data-id="element-2651">
          <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2652">
            INCLUYE
          </div>
          <ul className="space-y-1.5" data-id="element-2653">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[10px] leading-snug" data-id="element-2654">
                <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-2655" />
                <span data-id="element-2656">{f}</span>
              </li>)}
          </ul>
        </div>
        <div className="mt-5 pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-3" data-id="element-2657">
          <button className="flex-1 px-3 py-2 bg-[var(--text-ink)] text-white rounded-full text-[10px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-2658">
            {cta}
            <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-2659" />
          </button>
          <button className="px-3 py-2 border border-[var(--text-ink)] rounded-full text-[10px] font-medium" data-id="element-2660">
            Demo
          </button>
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  PACKS PAGE                                                         */
/* ================================================================== */
function PacksPageMockup() {
  return <BrowserFrame url="metroagency.co/packs" data-id="element-2661">
      <SiteNav active="packs" data-id="element-2662" />
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2663">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2664">
          PACKS · CINCO CATEGORÍAS
        </div>
        <div className="font-space font-bold text-5xl leading-[0.95] tracking-tight mb-3" data-id="element-2665">
          Packs Metro.
          <br data-id="element-2666" />
          <span className="text-[var(--text-muted)]" data-id="element-2667">
            Precio cerrado, IVA incluido.
          </span>
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-xl" data-id="element-2668">
          Web, Brand, Content, Marketing y 360°. Cinco familias con tiers
          internos — desde €390 hasta €8.900. Cada pack tiene CTA directo de
          compra y opción de agendar llamada de 15 min.
        </div>
        <div className="grid grid-cols-5 gap-2 mt-5" data-id="element-2669">
          {[['01', 'Web', '€390+'], ['02', 'Brand', '€1.200+'], ['03', 'Content', '€800+'], ['04', 'Marketing', '€350+/mes'], ['05', '360°', '€3.500+']].map(([n, l, p]) => <div key={l} className="bg-white border border-[var(--divider)] rounded-sm p-3" data-id="element-2670">
              <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2671">
                {n}
              </div>
              <div className="font-space font-bold text-base tracking-tight" data-id="element-2672">
                {l}
              </div>
              <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-wider mt-1" data-id="element-2673">
                {p}
              </div>
            </div>)}
        </div>
      </div>

      {/* 01 WEB */}
      <div className="px-6 py-10 bg-white" data-id="element-2674">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2675">
          <div data-id="element-2676">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2677">
              01 · WEB
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2678">
              Presencia online.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2679">
              Desde Micro (€390) hasta Premium (€4.800). Cuatro tiers, una sola
              línea editorial.
            </div>
          </div>
          <div className="text-right" data-id="element-2680">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2681">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2682">
              €390
            </div>
          </div>
        </div>
        <div className="mb-4" data-id="element-2683">
          <PackCard tier="TIER 0" tierLabel="MICRO · LA PUERTA DE ENTRADA" name="Web Micro" tagline="Existo online. Pack intencionalmente limitado para negocios ultra-pequeños sin presupuesto." price="€390" timeline="48–72 horas" ideal="Frutería, peluquería de barrio, autónomos" warnings={['One-page, sin navegación', 'Basado en plantilla (3 templates)', 'Sin formulario · solo WhatsApp', 'Sin Google Maps integrado', 'Sin hosting gestionado', '0 revisiones']} features={['Secciones básicas: nombre, horario, teléfono', 'Responsive (móvil + desktop)', 'Botón WhatsApp directo', 'Hasta 5 imágenes', 'SEO básico']} cta="Comprar Web Micro" accent="ink" data-id="element-2684" />
        </div>
        <div className="grid grid-cols-3 gap-4" data-id="element-2685">
          <PackCard tier="TIER 1" tierLabel="ESSENTIALS" name="Web Essentials" tagline="Landing funcional con formulario, mapa y SEO básico." price="€990" timeline="5–7 días" ideal="Peluquerías, talleres, electricistas" features={['1–2 secciones', 'Responsive', 'Formulario funcional', 'Google Maps', 'SEO básico', 'Hosting 1er año gratis', 'SSL', '1 revisión']} cta="Comprar Essentials" accent="blue" data-id="element-2686" />
          <PackCard tier="TIER 2" tierLabel="PROFESSIONAL" name="Web Professional" tagline="Sitio completo de 3–4 páginas con galería y soporte post-entrega." price="€2.400" timeline="10–14 días" ideal="Restaurantes, clínicas, tiendas" features={['Todo de Essentials, ampliado', '3–4 páginas', '20 imágenes IA', 'Google Business optimizado', 'Velocidad <2s', '2 revisiones', '1 mes soporte']} cta="Comprar Pro" accent="ink" data-id="element-2687" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Web Premium" tagline="5–6 páginas custom, reservas online, blog SEO, animaciones." price="€4.800" timeline="18–25 días" ideal="E-commerce, servicios premium" features={['Todo de Professional, ampliado', '5–6 páginas custom', 'Sistema de reservas', 'Blog + 3 artículos SEO', 'WhatsApp Business', 'Animaciones', '3 revisiones', '3 meses soporte']} cta="Comprar Premium" accent="ink" data-id="element-2688" />
        </div>
      </div>

      {/* 02 BRAND */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2689">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2690">
          <div data-id="element-2691">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2692">
              02 · BRAND
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2693">
              Identidad de marca.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2694">
              Independiente del sitio web — tu marca viaja con todo lo demás.
            </div>
          </div>
          <div className="text-right" data-id="element-2695">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2696">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2697">
              €1.200
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-2698">
          <PackCard tier="TIER 1" tierLabel="BASICS" name="Brand Basics" tagline="Logo, paleta, tipografía y aplicaciones — listo para arrancar." price="€1.200" timeline="7–10 días" ideal="Startups, identidad básica rápida" features={['Logo (2 conceptos, 1 ronda)', 'Paleta 3–4 colores', '1–2 tipografías', '10 aplicaciones mockup', 'Brand guidelines PDF (5pp)', 'Archivos PNG, SVG, PDF']} cta="Comprar Basics" accent="blue" data-id="element-2699" />
          <PackCard tier="TIER 3" tierLabel="PREMIUM" name="Brand Premium" tagline="Estrategia, positioning, sub-marcas y 30+ aplicaciones." price="€5.500" timeline="20–28 días" ideal="B2B, startups tech, coherencia total" features={['Todo de Basics, ampliado', '3 conceptos de logo', 'Sub-marca / iconos', 'Brand strategy workshop', 'Positioning + messaging', 'Guidelines (15–20pp)', 'Templates de presentación', '30+ aplicaciones']} cta="Comprar Premium" accent="ink" data-id="element-2700" />
        </div>
      </div>

      {/* 03 CONTENT */}
      <div className="px-6 py-10 bg-white" data-id="element-2701">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2702">
          <div data-id="element-2703">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2704">
              03 · CONTENT
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2705">
              Vídeo, foto y copy.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2706">
              Producción in-house en Barcelona.
            </div>
          </div>
          <div className="text-right" data-id="element-2707">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2708">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2709">
              €800
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4" data-id="element-2710">
          <PackCard tier="CONTENT" tierLabel="STARTER" name="Content Starter" tagline="1 vídeo + 20 imágenes IA + copy para landing." price="€1.400" timeline="7–10 días" ideal="Estrenar web o producto" features={['1 vídeo (30–60s)', '20 imágenes IA', 'Copy 1 página (600 palabras)', '1 revisión por entregable', 'Archivos web + social']} cta="Comprar Starter" accent="blue" data-id="element-2711" />
          <PackCard tier="CONTENT" tierLabel="VIDEO" name="Video Production" tagline="Día de rodaje + edición. Dos vídeos terminados." price="€1.800" timeline="10–14 días" ideal="Restaurantes, producto físico" features={['8h rodaje BCN', 'Cámara, sonido, luz', '2 vídeos (corto + largo)', 'Edición + color + subtítulos', '2 revisiones', 'Versiones IG/TikTok/YouTube']} cta="Comprar Video" accent="ink" data-id="element-2712" />
          <PackCard tier="CONTENT" tierLabel="PHOTO" name="Photo Session" tagline="Media jornada de fotografía local." price="€800" timeline="3–5 días" ideal="Tiendas, restaurantes" features={['4h en Barcelona', '30 fotos editadas', 'Retoque básico', 'Versiones web/social', '1 ronda de selección']} cta="Comprar Photo" accent="blue" data-id="element-2713" />
        </div>
      </div>

      {/* 04 MARKETING */}
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2714">
        <div className="flex items-baseline justify-between mb-5" data-id="element-2715">
          <div data-id="element-2716">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2717">
              04 · MARKETING
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2718">
              Always-on.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1 max-w-md" data-id="element-2719">
              Mensual sin permanencia — pausas o cancelas con 30 días de aviso.
            </div>
          </div>
          <div className="text-right" data-id="element-2720">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-2721">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl text-[var(--accent-blue)] leading-none mt-1" data-id="element-2722">
              €350<span className="text-base" data-id="element-2723">/mes</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4" data-id="element-2724">
          <PackCard tier="MONTHLY" tierLabel="SOCIAL" name="Social Management" tagline="Estrategia, calendario y 12 posts/mes." price="€450/mes" timeline="Setup 7d · Ongoing" ideal="Marcas locales, tiendas, servicios" features={['Audit + estrategia trimestral', 'Calendario editorial', '12 posts/mes', 'Gestión de DMs', 'Reporte mensual', 'Sin permanencia']} cta="Empezar Social" accent="blue" data-id="element-2725" />
          <PackCard tier="MONTHLY" tierLabel="SEO" name="SEO Monthly" tagline="Optimización + 1 artículo/mes." price="€350/mes" timeline="Audit 7d · Ongoing" ideal="Búsqueda orgánica" features={['Audit técnico inicial', 'Keywords mensuales', 'Optimización on-page', '1 artículo SEO/mes', 'Link building básico', 'Reporte ranking + tráfico']} cta="Empezar SEO" accent="ink" data-id="element-2726" />
          <PackCard tier="MONTHLY" tierLabel="PAID" name="Paid Media" tagline="Meta + Google Ads gestionado." price="€600/mes" timeline="Setup 5–7d · Ongoing" ideal="Presupuesto publicitario activo" features={['Setup Meta + Google', 'Tracking y pixel', '3 campañas activas', 'Optimización semanal', 'Reporte CPL/CPA', 'Coste medios aparte']} cta="Empezar Paid" accent="ink" data-id="element-2727" />
        </div>
      </div>

      {/* 05 360 */}
      <div className="px-6 py-10" style={{
      background: '#1A2BFF'
    }} data-id="element-2728">
        <div className="flex items-baseline justify-between mb-5 text-white" data-id="element-2729">
          <div data-id="element-2730">
            <div className="font-mono text-[9px] opacity-75 tracking-widest mb-1" data-id="element-2731">
              05 · 360° · SISTEMA COMPLETO
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-2732">
              Todo, en una sola línea.
            </div>
            <div className="text-[11px] opacity-85 mt-1 max-w-md" data-id="element-2733">
              Brand + Web + Content. Mismo equipo, mismo plazo, ahorro real.
            </div>
          </div>
          <div className="text-right" data-id="element-2734">
            <div className="font-mono text-[9px] opacity-75 tracking-widest" data-id="element-2735">
              DESDE
            </div>
            <div className="font-space font-bold text-4xl leading-none mt-1" data-id="element-2736">
              €3.500
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-2737">
          <PackCard tier="360°" tierLabel="BRAND + WEB" name="Brand + Web Combo" tagline="Identidad y sitio que la usa desde día 1." price="€3.500" timeline="14–18 días" ideal="Nuevos negocios" savings="Ahorro de €100 vs. separado" combo features={['Brand Basics completo', 'Web Professional completo', 'Integración desde día 1', 'Coherencia garantizada', 'Un solo plazo']} cta="Comprar Combo" accent="ink" data-id="element-2738" />
          <PackCard tier="360°" tierLabel="FULL SYSTEM" name="Full System" tagline="Brand + Web + Content. Todo el sistema." price="€8.900" timeline="25–30 días" ideal="Lanzamientos, rebrands completos" savings="Ahorro de €1.400 vs. separado" combo features={['Brand Premium completo', 'Web Premium completo', 'Content Starter:', '· 3 vídeos cortos', '· 40 imágenes IA', '· Copy completo del sitio', '· 5 posts social']} cta="Comprar Full System" accent="ink" data-id="element-2739" />
        </div>
      </div>

      {/* Add-ons */}
      <div className="px-6 py-10 bg-white" data-id="element-2740">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-2741">
          EXTRA · ADD-ONS
        </div>
        <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-2742">
          Servicios adicionales.
        </div>
        <div className="border border-[var(--divider)] rounded-sm overflow-hidden" data-id="element-2743">
          <div className="grid grid-cols-12 bg-[var(--text-ink)] text-white text-[9px] font-bold tracking-widest px-4 py-2.5" data-id="element-2744">
            <div className="col-span-6" data-id="element-2745">SERVICIO</div>
            <div className="col-span-2 text-right" data-id="element-2746">PRECIO</div>
            <div className="col-span-2 text-right" data-id="element-2747">PLAZO</div>
            <div className="col-span-2 text-right" data-id="element-2748">ACCIÓN</div>
          </div>
          {[['Pack imágenes IA (30)', '€150', '2–3 días', false], ['Video corto (30–60s)', '€350', '5–7 días', false], ['Copywriting (por página)', '€180', '2–3 días', false], ['Email marketing setup', '€250', '3–4 días', false], ['Foto/video shoot BCN', '€800', '1 día', false], ['Mantenimiento web', '€90/mes', 'Ongoing', true]].map(([n, p, t, r], i) => <div key={i} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--divider)] last:border-b-0 text-[10px] items-center" data-id="element-2749">
              <div className="col-span-6 flex items-center gap-2" data-id="element-2750">
                <span className="font-medium" data-id="element-2751">{n as string}</span>
                {r && <span className="text-[8px] font-mono text-[var(--accent-blue)] bg-[var(--bg-canvas)] px-1.5 py-0.5 rounded-full tracking-wider" data-id="element-2752">
                    RECURRENTE
                  </span>}
              </div>
              <div className="col-span-2 text-right font-space font-bold" data-id="element-2753">
                {p as string}
              </div>
              <div className="col-span-2 text-right text-[var(--text-muted)]" data-id="element-2754">
                {t as string}
              </div>
              <div className="col-span-2 text-right" data-id="element-2755">
                <button className="text-[9px] font-medium text-[var(--accent-blue)] underline" data-id="element-2756">
                  Añadir →
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {/* FAQ + Sticky */}
      <div className="px-6 py-12" style={{
      background: '#EDEEF1'
    }} data-id="element-2757">
        <div className="grid grid-cols-12 gap-6" data-id="element-2758">
          <div className="col-span-7" data-id="element-2759">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2760">
              PREGUNTAS FRECUENTES
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-2761">
              Antes de comprar.
            </div>
            <div className="space-y-2" data-id="element-2762">
              {[['¿Los precios incluyen IVA?', 'Sí. Todos los packs muestran el precio final con IVA. Lo que ves es lo que pagas.'], ['¿Cómo se paga?', '50% al inicio del proyecto y 50% antes de la entrega. Transferencia o Stripe.'], ['¿Hay garantía?', '14 días post-entrega para corregir errores del trabajo realizado.'], ['¿Puedo upgradear?', 'Sí. El importe pagado se descuenta del pack superior en los primeros 60 días.']].map(([q, a], i) => <div key={i} className="bg-white rounded-sm border border-[var(--divider)] p-4" data-id="element-2763">
                  <div className="flex items-start justify-between gap-3" data-id="element-2764">
                    <div className="font-space font-bold text-sm" data-id="element-2765">{q}</div>
                    <PlusIcon className="w-3.5 h-3.5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-2766" />
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] leading-relaxed mt-2" data-id="element-2767">
                    {a}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="col-span-5" data-id="element-2768">
            <div className="bg-[var(--text-ink)] text-white rounded-sm p-6 sticky top-4" data-id="element-2769">
              <div className="font-mono text-[9px] tracking-widest opacity-70 mb-2" data-id="element-2770">
                ¿NO LO TIENES CLARO?
              </div>
              <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-3" data-id="element-2771">
                Empieza por una conversación.
              </div>
              <div className="text-[11px] opacity-80 leading-relaxed mb-4" data-id="element-2772">
                Cuéntanos qué necesitas, en 15 minutos te decimos qué pack
                encaja.
              </div>
              <button className="w-full px-4 py-2.5 bg-white text-[var(--text-ink)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2 mb-2" data-id="element-2773">
                Agendar llamada · 15 min
                <ArrowRightIcon className="w-3 h-3" data-id="element-2774" />
              </button>
              <button className="w-full px-4 py-2.5 border border-white/30 rounded-full text-xs font-medium" data-id="element-2775">
                Enviar mensaje
              </button>
              <div className="border-t border-white/20 mt-5 pt-4 text-[9px] opacity-60 font-mono tracking-widest" data-id="element-2776">
                PAGO SEGURO · STRIPE · 50/50
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter data-id="element-2777" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  TERMS PAGE                                                         */
/* ================================================================== */
function TermsPageMockup() {
  return <BrowserFrame url="metroagency.co/terminos" data-id="element-2778">
      <SiteNav active="terms" data-id="element-2779" />
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2780">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2781">
          LEGAL · METRO AGENCY S.L.
        </div>
        <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-2782">
          Términos
          <br data-id="element-2783" />y condiciones.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2784">
          Las reglas del juego, en castellano y sin letra pequeña confusa. Si
          algo no está claro, pregúntanos antes de firmar.
        </div>
        <div className="mt-4 flex items-center gap-3 text-[10px] text-[var(--text-muted)]" data-id="element-2785">
          <span className="font-mono tracking-wider" data-id="element-2786">
            ÚLTIMA ACTUALIZACIÓN · [ FECHA ]
          </span>
          <span data-id="element-2787">·</span>
          <span className="font-mono tracking-widest" data-id="element-2788">v1.0</span>
        </div>
      </div>
      <div className="px-6 py-10 grid grid-cols-12 gap-6" data-id="element-2789">
        <div className="col-span-4" data-id="element-2790">
          <div className="sticky top-4" data-id="element-2791">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2792">
              ÍNDICE
            </div>
            <div className="space-y-1.5" data-id="element-2793">
              {['01 · Sobre Metro Agency', '02 · Servicios contratados', '03 · Condiciones de pago', '04 · Plazos y entregas', '05 · Revisiones', '06 · Devoluciones', '07 · Garantía', '08 · Propiedad intelectual', '09 · Confidencialidad', '10 · Cancelaciones', '11 · Limitación responsabilidad', '12 · RGPD', '13 · Cookies', '14 · Modificaciones', '15 · Jurisdicción'].map((t, i) => <a key={i} className={'block text-[10px] py-1 ' + (i === 2 ? 'text-[var(--accent-blue)] font-medium' : 'text-[var(--text-muted)]')} data-id="element-2794">
                  {t}
                </a>)}
            </div>
            <div className="mt-5 pt-4 border-t border-[var(--divider)]" data-id="element-2795">
              <div className="text-[9px] text-[var(--text-muted)] leading-relaxed" data-id="element-2796">
                ¿Dudas legales?
                <br data-id="element-2797" />
                <span className="text-[var(--accent-blue)] font-medium" data-id="element-2798">
                  legal@metroagency.co
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-8 space-y-6" data-id="element-2799">
          {[{
          n: '01',
          t: 'Sobre Metro Agency',
          p: 'Metro Agency S.L., NIF B12345678, Carrer de la Marina 145, 08013 Barcelona. Inscrita en el Registro Mercantil de Barcelona. [ Texto pendiente de asesoría. ]'
        }, {
          n: '02',
          t: 'Servicios contratados',
          p: 'Los packs descritos en /packs constituyen una oferta cerrada con alcance, plazo y precio. La contratación implica aceptación íntegra de estos términos.'
        }, {
          n: '03',
          t: 'Condiciones de pago',
          p: 'Precios incluyen IVA 21% (Ley 37/1992). Pago en dos hitos: 50% inicio + 50% antes de entrega. SEPA o Stripe.'
        }, {
          n: '06',
          t: 'Política de devoluciones',
          p: 'Art. 103.a RD 1/2007: servicios digitales personalizados quedan excluidos del desistimiento una vez iniciada la ejecución. 14 días para servicios no iniciados.'
        }, {
          n: '07',
          t: 'Garantía post-entrega',
          p: '14 días naturales desde entrega para corregir defectos imputables al trabajo. No cubre modificaciones posteriores del cliente.'
        }, {
          n: '12',
          t: 'RGPD',
          p: 'Tratamiento conforme Reglamento (UE) 2016/679 y LOPDGDD. Derechos en privacy@metroagency.co.'
        }, {
          n: '15',
          t: 'Jurisdicción aplicable',
          p: 'Juzgados y Tribunales de Barcelona para cualquier controversia.'
        }].map(s => <div key={s.n} className="border-t border-[var(--divider)] pt-4" data-id="element-2800">
              <div className="flex items-baseline gap-3 mb-2" data-id="element-2801">
                <span className="font-mono text-[10px] font-bold text-[var(--accent-blue)] tracking-widest" data-id="element-2802">
                  {s.n}
                </span>
                <div className="font-space font-bold text-lg leading-tight tracking-tight" data-id="element-2803">
                  {s.t}
                </div>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-2804">
                {s.p}
              </div>
            </div>)}
          <div className="text-white p-5 mt-4" style={{
          background: '#1A2BFF'
        }} data-id="element-2805">
            <div className="font-mono text-[9px] opacity-80 tracking-widest mb-2" data-id="element-2806">
              ACEPTACIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight tracking-tight mb-2" data-id="element-2807">
              La contratación implica aceptación.
            </div>
            <div className="text-[11px] opacity-90 leading-relaxed" data-id="element-2808">
              Si algo no encaja antes de firmar, escríbenos a
              legal@metroagency.co.
            </div>
          </div>
        </div>
      </div>
      <SiteFooter data-id="element-2809" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  CONTACT PAGE                                                       */
/* ================================================================== */
function ContactPageMockup() {
  return <BrowserFrame url="metroagency.co/contacto" data-id="element-2810">
      <SiteNav active="contacto" data-id="element-2811" />
      <div className="px-6 py-12" style={{
      background: '#0E0E12'
    }} data-id="element-2812">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2813">
          CONTACTO · CANAL DIRECTO
        </div>
        <div className="font-space font-bold text-5xl leading-[0.95] tracking-tight text-white mb-4" data-id="element-2814">
          Hablemos.
          <br data-id="element-2815" />
          <span className="text-white/60" data-id="element-2816">Sin formulario eterno.</span>
        </div>
        <div className="font-helvetica text-sm text-white/70 leading-relaxed max-w-lg mb-5" data-id="element-2817">
          Respuesta en menos de 24h laborables. Te decimos qué encaja, qué no, y
          cuánto cuesta — antes de pedirte una firma.
        </div>
        <div className="flex items-center gap-4 text-[10px] text-white/50 font-mono tracking-wider" data-id="element-2818">
          <span data-id="element-2819">· &lt; 24H</span>
          <span data-id="element-2820">· PRESUPUESTO GRATIS</span>
          <span data-id="element-2821">· SIN COMPROMISO</span>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-0" data-id="element-2822">
        <div className="col-span-7 px-6 py-10 bg-white border-r border-[var(--divider)]" data-id="element-2823">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2824">
            FORMULARIO · * OBLIGATORIOS
          </div>
          <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-5" data-id="element-2825">
            Cuéntanos.
          </div>
          <div className="space-y-4" data-id="element-2826">
            <div className="grid grid-cols-2 gap-3" data-id="element-2827">
              <div data-id="element-2828">
                <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1.5" data-id="element-2829">
                  NOMBRE *
                </div>
                <div className="border-b border-[var(--text-ink)] py-2 text-[11px] text-[var(--text-muted)]" data-id="element-2830">
                  Marta García
                </div>
              </div>
              <div data-id="element-2831">
                <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1.5" data-id="element-2832">
                  EMAIL *
                </div>
                <div className="border-b border-[var(--text-ink)] py-2 text-[11px] text-[var(--text-muted)]" data-id="element-2833">
                  marta@negocio.com
                </div>
              </div>
            </div>
            <div data-id="element-2834">
              <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1.5" data-id="element-2835">
                ¿EN QUÉ PODEMOS AYUDARTE? *
              </div>
              <div className="border border-[var(--text-ink)] rounded-sm px-3 py-2.5 text-[11px] flex items-center justify-between bg-white" data-id="element-2836">
                <span className="font-medium" data-id="element-2837">Quiero una web nueva</span>
                <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-2838" />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-2839">
                {['Web nueva', 'Redes', 'Branding', 'Foto · Vídeo', 'Evento', 'IA', 'Otro'].map((t, i) => <span key={t} className={'text-[9px] px-2 py-0.5 rounded-full border ' + (i === 0 ? 'bg-[var(--text-ink)] text-white border-[var(--text-ink)]' : 'border-[var(--divider)] text-[var(--text-muted)]')} data-id="element-2840">
                    {t}
                  </span>)}
              </div>
            </div>
            <div data-id="element-2841">
              <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1.5" data-id="element-2842">
                CUÉNTANOS MÁS
              </div>
              <div className="border border-[var(--divider)] rounded-sm p-3 text-[11px] text-[var(--text-muted)] leading-relaxed h-20" data-id="element-2843">
                Tengo un restaurante en Gracia, necesito web nueva. Reservas
                integradas si es posible.
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-2844">
              <div className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider" data-id="element-2845">
                AL ENVIAR ACEPTAS LA POLÍTICA
              </div>
              <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2846">
                Enviar mensaje
                <SendIcon className="w-3 h-3" data-id="element-2847" />
              </button>
            </div>
          </div>
        </div>
        <div className="col-span-5 px-6 py-10 bg-[#EDEEF1]" data-id="element-2848">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2849">
            CANALES DIRECTOS
          </div>
          <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-5" data-id="element-2850">
            O escríbenos
            <br data-id="element-2851" />
            directamente.
          </div>
          <div className="space-y-3" data-id="element-2852">
            {[{
            Icon: MessageCircleIcon,
            l: 'WhatsApp',
            v: '+34 931 23 45 67',
            n: 'Respuesta < 2h laboral'
          }, {
            Icon: MailIcon,
            l: 'Email',
            v: 'hello@metroagency.co',
            n: 'Proyectos y presupuestos'
          }, {
            Icon: InstagramIcon,
            l: 'Instagram',
            v: '@metroagency.co',
            n: 'DMs abiertas'
          }, {
            Icon: MapPinIcon,
            l: 'Estudio',
            v: 'Carrer Marina 145',
            n: 'Barcelona · Con cita previa'
          }].map(c => <div key={c.l} className="bg-white border border-[var(--divider)] rounded-sm p-3 flex items-start gap-3" data-id="element-2853">
                <div className="w-8 h-8 rounded-sm bg-[var(--text-ink)] text-white flex items-center justify-center flex-shrink-0" data-id="element-2854">
                  <c.Icon className="w-3.5 h-3.5" strokeWidth={1.75} data-id="element-2855" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2856">
                  <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest" data-id="element-2857">
                    {c.l.toUpperCase()}
                  </div>
                  <div className="font-space font-bold text-sm tracking-tight leading-tight truncate" data-id="element-2858">
                    {c.v}
                  </div>
                  <div className="text-[9px] text-[var(--text-muted)] leading-snug mt-0.5" data-id="element-2859">
                    {c.n}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <SiteFooter data-id="element-2860" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  REQUEST FORMS PAGE                                                 */
/* ================================================================== */
function RequestFormsPageMockup() {
  const tabs = [{
    k: 'web',
    l: 'Web',
    active: true
  }, {
    k: 'rrss',
    l: 'Redes'
  }, {
    k: 'brand',
    l: 'Branding'
  }, {
    k: 'foto',
    l: 'Foto · Vídeo'
  }, {
    k: 'evento',
    l: 'Evento'
  }];
  return <BrowserFrame url="metroagency.co/solicitar/web" data-id="element-2861">
      <SiteNav active="solicitar" data-id="element-2862" />
      <div className="px-6 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-2863">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2864">
          SOLICITAR PRESUPUESTO · FORMULARIO POR SERVICIO
        </div>
        <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-2865">
          Cuéntanos
          <br data-id="element-2866" />
          tu proyecto.
        </div>
        <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed max-w-lg" data-id="element-2867">
          5 formularios específicos — uno por familia de servicio.
        </div>
      </div>
      <div className="px-6 pt-6 bg-white border-b border-[var(--divider)]" data-id="element-2868">
        <div className="flex items-center gap-1" data-id="element-2869">
          {tabs.map(t => <div key={t.k} className={'px-4 py-2.5 text-[11px] font-medium border-b-2 -mb-px ' + (t.active ? 'border-[var(--accent-blue)] text-[var(--accent-blue)]' : 'border-transparent text-[var(--text-muted)]')} data-id="element-2870">
              {t.l}
            </div>)}
        </div>
      </div>
      <div className="px-6 py-8 bg-white grid grid-cols-12 gap-6" data-id="element-2871">
        <div className="col-span-8 space-y-5" data-id="element-2872">
          <div data-id="element-2873">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2874">
              FORMULARIO 01
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight" data-id="element-2875">
              Quiero una web nueva.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-canvas)]" data-id="element-2876">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-2877">
              DATOS COMUNES · TODOS LOS FORMULARIOS
            </div>
            <div className="grid grid-cols-2 gap-3 text-[10px]" data-id="element-2878">
              {[['NOMBRE Y EMPRESA *', 'Marta · Cafetería La Marina'], ['EMAIL · WHATSAPP *', 'marta@lamarina.cafe · +34 600 000 000'], ['PLAZO', 'En 1 mes'], ['PRESUPUESTO', '€1.000 – €3.000']].map(([l, v]) => <div key={l} data-id="element-2879">
                  <div className="text-[8px] font-mono tracking-widest text-[var(--text-muted)] mb-1" data-id="element-2880">
                    {l}
                  </div>
                  <div className="border-b border-[var(--text-ink)] py-1.5 text-[var(--text-muted)]" data-id="element-2881">
                    {v}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="space-y-4" data-id="element-2882">
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest" data-id="element-2883">
              ESPECÍFICOS · WEB NUEVA
            </div>
            <div data-id="element-2884">
              <div className="text-[9px] font-mono tracking-widest text-[var(--text-muted)] mb-2" data-id="element-2885">
                TIPO DE WEB *
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-2886">
                {['Informativa', 'Tienda online', 'Con blog', 'Portfolio', 'Reservas', 'Otra'].map((t, i) => <span key={t} className={'text-[10px] px-2.5 py-1 rounded-full border ' + (i === 4 ? 'bg-[var(--text-ink)] text-white border-[var(--text-ink)]' : 'border-[var(--divider)] text-[var(--text-muted)]')} data-id="element-2887">
                    {t}
                  </span>)}
              </div>
            </div>
            <div data-id="element-2888">
              <div className="text-[9px] font-mono tracking-widest text-[var(--text-muted)] mb-2" data-id="element-2889">
                EXTRAS
              </div>
              <div className="grid grid-cols-3 gap-1.5" data-id="element-2890">
                {[['SEO básico', true], ['Integración redes', true], ['Google Business', false], ['Reservas online', true], ['Formulario contacto', true], ['Email marketing', false]].map(([t, on]) => <div key={t as string} className={'flex items-center gap-2 text-[10px] px-2 py-1.5 rounded-sm border ' + (on ? 'border-[var(--text-ink)] bg-white' : 'border-[var(--divider)] bg-[var(--bg-canvas)] text-[var(--text-muted)]')} data-id="element-2891">
                    <div className={'w-3 h-3 border rounded-[2px] flex items-center justify-center ' + (on ? 'bg-[var(--accent-blue)] border-[var(--accent-blue)]' : 'border-[var(--divider)]')} data-id="element-2892">
                      {on && <CheckIcon className="w-2 h-2 text-white" strokeWidth={3} data-id="element-2893" />}
                    </div>
                    <span data-id="element-2894">{t as string}</span>
                  </div>)}
              </div>
            </div>
            <div data-id="element-2895">
              <div className="text-[9px] font-mono tracking-widest text-[var(--text-muted)] mb-1.5" data-id="element-2896">
                DESCRIBE TU NEGOCIO
              </div>
              <div className="border border-[var(--divider)] rounded-sm p-2.5 text-[10px] text-[var(--text-muted)] leading-relaxed h-14" data-id="element-2897">
                Cafetería de especialidad en Gracia. Mesa para 22, foco en
                brunch.
              </div>
            </div>
          </div>
          <div className="pt-4 flex items-center justify-between border-t border-[var(--divider)]" data-id="element-2898">
            <div className="text-[9px] font-mono text-[var(--text-muted)] tracking-wider" data-id="element-2899">
              CAMPOS CON * SON OBLIGATORIOS
            </div>
            <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2900">
              Solicitar presupuesto
              <ArrowRightIcon className="w-3 h-3" data-id="element-2901" />
            </button>
          </div>
        </div>
        <div className="col-span-4 space-y-4" data-id="element-2902">
          <div className="bg-[var(--text-ink)] text-white rounded-sm p-4" data-id="element-2903">
            <div className="font-mono text-[9px] tracking-widest opacity-70 mb-1.5" data-id="element-2904">
              RESPUESTA
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-2905">
              Presupuesto cerrado en 24h.
            </div>
            <div className="text-[10px] opacity-80 leading-relaxed" data-id="element-2906">
              Sin sorpresas, sin asteriscos.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-4" data-id="element-2907">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-2908">
              OTROS FORMULARIOS
            </div>
            <div className="space-y-2" data-id="element-2909">
              {['Redes sociales', 'Branding', 'Foto · Vídeo', 'Evento', 'AI Solutions'].map(l => <div key={l} className="flex items-center justify-between text-[10px] py-1 border-b border-[var(--divider)] last:border-b-0" data-id="element-2910">
                  <span className="font-medium" data-id="element-2911">{l}</span>
                  <span className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-2912">
                    →
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      <SiteFooter data-id="element-2913" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  PORTFOLIO PAGE                                                     */
/* ================================================================== */
function PortfolioPageMockup() {
  const filters = [{
    k: 'all',
    l: 'Todos',
    n: 36,
    active: true
  }, {
    k: 'web',
    l: 'Web',
    n: 12
  }, {
    k: 'rrss',
    l: 'Redes',
    n: 8
  }, {
    k: 'brand',
    l: 'Branding',
    n: 7
  }, {
    k: 'foto',
    l: 'Foto',
    n: 5
  }, {
    k: 'video',
    l: 'Vídeo',
    n: 4
  }, {
    k: 'evento',
    l: 'Eventos',
    n: 3
  }];
  const projects = [{
    cat: 'WEB',
    catColor: '#1A2BFF',
    name: 'Cafetería La Marina',
    brief: 'Web con reservas · Gracia, BCN',
    bg: '#EDEEF1',
    span: 'col-span-8'
  }, {
    cat: 'BRANDING',
    catColor: '#F26A35',
    name: 'Òrbita Studio',
    brief: 'Identidad completa · Arquitectura',
    bg: '#0E0E12',
    dark: true,
    span: 'col-span-4'
  }, {
    cat: 'VIDEO',
    catColor: '#2A8E47',
    name: 'Salt & Iron',
    brief: 'Vídeo de marca + reels',
    bg: '#1A2BFF',
    dark: true,
    span: 'col-span-4'
  }, {
    cat: 'WEB',
    catColor: '#1A2BFF',
    name: 'Clínica Aleta',
    brief: 'Web + chatbot AI · Dental',
    bg: '#EDEEF1',
    span: 'col-span-4'
  }, {
    cat: 'FOTO',
    catColor: '#E8C547',
    name: 'Mercat Princesa',
    brief: 'Reportaje producto + corporativo',
    bg: '#F4A523',
    span: 'col-span-4'
  }, {
    cat: 'REDES',
    catColor: '#F4A523',
    name: 'Casa Olá',
    brief: 'Gestión social · 6 meses',
    bg: '#EDEEF1',
    span: 'col-span-6'
  }, {
    cat: 'EVENTO',
    catColor: '#F26A35',
    name: 'Brunch Festival 26',
    brief: 'Cobertura completa · 800 asistentes',
    bg: '#0E0E12',
    dark: true,
    span: 'col-span-6'
  }];
  return <BrowserFrame url="metroagency.co/portfolio" data-id="element-2914">
      <SiteNav active="portfolio" data-id="element-2915" />
      <div className="px-6 py-12" style={{
      background: '#0E0E12'
    }} data-id="element-2916">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2917">
          PORTFOLIO · TRABAJOS REALES
        </div>
        <div className="font-space font-bold text-5xl leading-[0.95] tracking-tight text-white mb-3" data-id="element-2918">
          El trabajo
          <br data-id="element-2919" />
          <span className="text-white/60" data-id="element-2920">habla solo.</span>
        </div>
        <div className="font-helvetica text-sm text-white/70 leading-relaxed max-w-lg mb-6" data-id="element-2921">
          36 proyectos, 7 sectores, una línea visual.
        </div>
        <div className="grid grid-cols-4 gap-4 pt-5 border-t border-white/15" data-id="element-2922">
          {[['36', 'Proyectos'], ['7', 'Categorías'], ['18', 'Sectores'], ['2019', 'Desde']].map(([n, l]) => <div key={l} data-id="element-2923">
              <div className="font-space font-bold text-3xl text-white tracking-tight leading-none" data-id="element-2924">
                {n}
              </div>
              <div className="font-mono text-[9px] text-white/50 tracking-widest mt-1" data-id="element-2925">
                {l}
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-6 py-4 bg-white border-b border-[var(--divider)] sticky top-0 z-10" data-id="element-2926">
        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-2927">
          {filters.map(f => <div key={f.k} className={'text-[10px] px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ' + (f.active ? 'bg-[var(--text-ink)] text-white border-[var(--text-ink)]' : 'border-[var(--divider)] text-[var(--text-muted)]')} data-id="element-2928">
              <span className="font-medium" data-id="element-2929">{f.l}</span>
              <span className="font-mono text-[8px] opacity-60" data-id="element-2930">{f.n}</span>
            </div>)}
        </div>
      </div>
      <div className="px-6 py-8 bg-white" data-id="element-2931">
        <div className="grid grid-cols-12 gap-3" data-id="element-2932">
          {projects.map((p, i) => <div key={i} className={'rounded-sm overflow-hidden border border-[var(--divider)] ' + p.span} style={{
          background: p.bg
        }} data-id="element-2933">
              <div className={'aspect-[16/10] flex flex-col justify-between p-4 ' + (p.dark ? 'text-white' : '')} data-id="element-2934">
                <div className="flex items-start justify-between" data-id="element-2935">
                  <div className="font-mono text-[8px] tracking-widest px-1.5 py-0.5 rounded-sm font-bold" style={{
                background: p.catColor,
                color: '#fff'
              }} data-id="element-2936">
                    {p.cat}
                  </div>
                  <div className={'font-mono text-[8px] tracking-widest ' + (p.dark ? 'text-white/50' : 'text-[var(--text-muted)]')} data-id="element-2937">
                    2026
                  </div>
                </div>
                <div data-id="element-2938">
                  <div className="font-space font-bold text-xl leading-tight tracking-tight mb-1" data-id="element-2939">
                    {p.name}
                  </div>
                  <div className={'text-[10px] leading-snug mb-3 ' + (p.dark ? 'text-white/70' : 'text-[var(--text-muted)]')} data-id="element-2940">
                    {p.brief}
                  </div>
                  <div className={'inline-flex items-center gap-1.5 text-[10px] font-medium ' + (p.dark ? 'text-white' : 'text-[var(--accent-blue)]')} data-id="element-2941">
                    Ver proyecto
                    <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-2942" />
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-6 py-10" style={{
      background: '#1A2BFF'
    }} data-id="element-2943">
        <div className="grid grid-cols-12 gap-4 items-center text-white" data-id="element-2944">
          <div className="col-span-8" data-id="element-2945">
            <div className="font-mono text-[9px] opacity-75 tracking-widest mb-2" data-id="element-2946">
              ¿VES ALGO QUE TE GUSTA?
            </div>
            <div className="font-space font-bold text-3xl tracking-tight leading-tight mb-2" data-id="element-2947">
              Podemos hacer algo así para ti. O mejor.
            </div>
          </div>
          <div className="col-span-4 flex flex-col gap-2" data-id="element-2948">
            <button className="px-4 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2" data-id="element-2949">
              Solicitar presupuesto
              <ArrowRightIcon className="w-3 h-3" data-id="element-2950" />
            </button>
          </div>
        </div>
      </div>
      <SiteFooter data-id="element-2951" />
    </BrowserFrame>;
}
/* ================================================================== */
/*  QR · SERVICIOS — DESKTOP                                           */
/* ================================================================== */
function QRServicesDesktopView() {
  return <BrowserFrame url="me-tro.co/q/servicios" data-id="element-2952">
      <div className="px-8 py-12" style={{
      background: '#0E0E12'
    }} data-id="element-2953">
        <div className="grid grid-cols-12 gap-6 items-center" data-id="element-2954">
          <div className="col-span-7" data-id="element-2955">
            <div className="flex items-center gap-3 mb-5" data-id="element-2956">
              <img src={METRO_LOGO_URL} alt="me—tro" className="h-4 w-auto" data-id="element-2957" />
              <div className="font-mono text-[9px] text-white/40 tracking-widest" data-id="element-2958">
                QR · /SERVICIOS · BARCELONA
              </div>
            </div>
            <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-3" data-id="element-2959">
              AGENCIA CREATIVA · BCN
            </div>
            <div className="font-space font-bold text-6xl leading-[0.92] tracking-tight text-white mb-5" data-id="element-2960">
              Tu negocio.
              <br data-id="element-2961" />
              <span className="text-[var(--accent-blue)]" data-id="element-2962">
                Con mejor cara online.
              </span>
            </div>
            <div className="font-helvetica text-sm text-white/70 leading-relaxed max-w-lg mb-6" data-id="element-2963">
              Web, redes sociales, branding, foto y vídeo. Todo lo que necesitas
              para destacar — en un solo estudio.
            </div>
            <div className="flex items-center gap-3" data-id="element-2964">
              <button className="px-5 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-2965">
                Ver qué hacemos
                <ArrowRightIcon className="w-3 h-3" data-id="element-2966" />
              </button>
              <button className="px-5 py-2.5 border border-white/30 text-white rounded-full text-xs font-medium" data-id="element-2967">
                Hablar ahora
              </button>
            </div>
          </div>
          <div className="col-span-5" data-id="element-2968">
            <div className="bg-white/5 border border-white/10 rounded-sm p-5" data-id="element-2969">
              <div className="font-mono text-[8px] text-white/50 tracking-widest mb-3" data-id="element-2970">
                LA RED METRO
              </div>
              <ServiceNetwork height={180} data-id="element-2971" />
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/15" data-id="element-2972">
                {[['+120', 'PROYECTOS'], ['7', 'AÑOS'], ['24H', 'RESPUESTA']].map(([n, l]) => <div key={l} data-id="element-2973">
                    <div className="font-space font-bold text-xl text-white tracking-tight leading-none" data-id="element-2974">
                      {n}
                    </div>
                    <div className="font-mono text-[8px] text-white/50 tracking-widest mt-1" data-id="element-2975">
                      {l}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-8 py-4 bg-white border-b border-[var(--divider)]" data-id="element-2976">
        <div className="grid grid-cols-4 gap-6" data-id="element-2977">
          {[{
          Icon: ClockIcon,
          l: 'Desde 72h'
        }, {
          Icon: MapPinIcon,
          l: 'Remoto o BCN'
        }, {
          Icon: CheckIcon,
          l: 'Precio claro'
        }, {
          Icon: SparklesIcon,
          l: 'Equipo único'
        }].map(c => <div key={c.l} className="flex items-center gap-2" data-id="element-2978">
              <c.Icon className="w-4 h-4 text-[var(--accent-blue)]" strokeWidth={1.75} data-id="element-2979" />
              <div className="font-space font-bold text-xs" data-id="element-2980">{c.l}</div>
            </div>)}
        </div>
      </div>
      <div className="px-8 py-10 bg-white" data-id="element-2981">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2982">
          QUÉ NECESITA TU NEGOCIO
        </div>
        <div className="font-space font-bold text-3xl tracking-tight leading-tight mb-5" data-id="element-2983">
          Seis familias. Una línea.
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-2984">
          {[{
          l: 'Web',
          d: 'Tu escaparate online.',
          c: '#1A2BFF',
          f: '€390'
        }, {
          l: 'Redes Sociales',
          d: 'Contenido que engancha.',
          c: '#F26A35',
          f: '€450/mes'
        }, {
          l: 'Branding',
          d: 'Identidad reconocible.',
          c: '#E8C547',
          f: '€1.200'
        }, {
          l: 'Foto · Vídeo',
          d: 'Imagen que vende.',
          c: '#2A8E47',
          f: '€800'
        }, {
          l: 'Eventos',
          d: 'Cobertura profesional.',
          c: '#F4A523',
          f: 'A medida'
        }, {
          l: 'AI Solutions',
          d: 'Bots, agentes y GPTs.',
          c: '#0E0E12',
          f: '€490'
        }].map(s => <div key={s.l} className="border border-[var(--divider)] rounded-sm p-4" data-id="element-2985">
              <div className="flex items-center justify-between mb-3" data-id="element-2986">
                <div className="w-3 h-3 rounded-sm" style={{
              background: s.c
            }} data-id="element-2987" />
                <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-2988">
                  DESDE {s.f}
                </div>
              </div>
              <div className="font-space font-bold text-lg tracking-tight leading-tight mb-1" data-id="element-2989">
                {s.l}
              </div>
              <div className="text-[11px] text-[var(--text-muted)] leading-snug mb-3" data-id="element-2990">
                {s.d}
              </div>
              <div className="text-[10px] text-[var(--accent-blue)] font-medium inline-flex items-center gap-1.5" data-id="element-2991">
                Ver detalle
                <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-2992" />
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-8 py-10" style={{
      background: '#1A2BFF'
    }} data-id="element-2993">
        <div className="grid grid-cols-12 gap-6 items-center text-white" data-id="element-2994">
          <div className="col-span-7" data-id="element-2995">
            <div className="flex items-center gap-2 mb-3" data-id="element-2996">
              <SparklesIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-2997" />
              <div className="font-mono text-[9px] opacity-75 tracking-widest" data-id="element-2998">
                OFERTA · WEB EXPRESS
              </div>
            </div>
            <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-2999">
              Web profesional en 72h.
              <br data-id="element-3000" />
              <span className="bg-white text-[var(--accent-blue)] px-2" data-id="element-3001">
                €390.
              </span>
            </div>
            <div className="text-sm opacity-85 leading-relaxed max-w-md mb-4" data-id="element-3002">
              Diseño limpio, dominio guiado, hosting el primer año y SEO básico.
              Todo incluido.
            </div>
            <button className="px-5 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-3003">
              Saber más
              <ArrowRightIcon className="w-3 h-3" data-id="element-3004" />
            </button>
          </div>
          <div className="col-span-5 grid grid-cols-2 gap-2" data-id="element-3005">
            {[['72H', 'Entrega'], ['5', 'Páginas'], ['1', 'Año hosting'], ['30D', 'Soporte']].map(([n, l]) => <div key={l} className="bg-white/10 border border-white/20 rounded-sm p-4" data-id="element-3006">
                <div className="font-space font-bold text-3xl tracking-tight leading-none" data-id="element-3007">
                  {n}
                </div>
                <div className="font-mono text-[9px] tracking-widest opacity-70 mt-1" data-id="element-3008">
                  {l}
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="px-8 py-10 bg-white" data-id="element-3009">
        <div className="flex items-end justify-between mb-4" data-id="element-3010">
          <div data-id="element-3011">
            <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-3012">
              SELECTED WORK
            </div>
            <div className="font-space font-bold text-2xl tracking-tight leading-tight" data-id="element-3013">
              Algunos trabajos.
            </div>
          </div>
          <div className="text-[11px] text-[var(--accent-blue)] font-medium" data-id="element-3014">
            Ver portfolio →
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3" data-id="element-3015">
          {[{
          l: 'La Marina',
          cat: 'WEB',
          c: '#EDEEF1'
        }, {
          l: 'Òrbita',
          cat: 'BRAND',
          c: '#0E0E12',
          dark: true
        }, {
          l: 'Aleta',
          cat: 'WEB · AI',
          c: '#1A2BFF',
          dark: true
        }, {
          l: 'Salt & Iron',
          cat: 'VIDEO',
          c: '#F26A35',
          dark: true
        }].map(p => <div key={p.l} className="aspect-[4/3] rounded-sm p-3 flex flex-col justify-between" style={{
          background: p.c,
          color: p.dark ? '#fff' : '#0E0E12'
        }} data-id="element-3016">
              <div className={'font-mono text-[8px] tracking-widest ' + (p.dark ? 'opacity-70' : 'opacity-60')} data-id="element-3017">
                {p.cat}
              </div>
              <div className="font-space font-bold text-base tracking-tight leading-tight" data-id="element-3018">
                {p.l}
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-8 py-10" style={{
      background: '#EDEEF1'
    }} data-id="element-3019">
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-3020">
          LO QUE DICEN
        </div>
        <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-5" data-id="element-3021">
          Clientes reales, palabras reales.
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-3022">
          {[{
          q: 'En 4 días teníamos web nueva y reservas funcionando. El presupuesto fue lo prometido.',
          n: 'Marta García',
          r: 'Cafetería La Marina · BCN'
        }, {
          q: 'No me trataron como "negocio pequeño". Trabajaron mi branding con el mismo cuidado.',
          n: 'Daniel Vázquez',
          r: 'Òrbita Studio'
        }, {
          q: 'El chatbot atiende DMs mientras duermo. Pago €49/mes y me ahorra 2h diarias.',
          n: 'Clara Roig',
          r: 'Clínica Aleta'
        }].map(t => <div key={t.n} className="bg-white border border-[var(--divider)] rounded-sm p-4" data-id="element-3023">
              <div className="flex gap-0.5 mb-3" data-id="element-3024">
                {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-3 h-3 text-[var(--accent-blue)] fill-[var(--accent-blue)]" data-id="element-3025" />)}
              </div>
              <div className="text-[11px] leading-relaxed mb-3 italic" data-id="element-3026">
                "{t.q}"
              </div>
              <div className="font-space font-bold text-xs" data-id="element-3027">{t.n}</div>
              <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider mt-0.5" data-id="element-3028">
                {t.r}
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-8 py-12 bg-[var(--text-ink)] text-white" data-id="element-3029">
        <div className="grid grid-cols-12 gap-6 items-center" data-id="element-3030">
          <div className="col-span-8" data-id="element-3031">
            <div className="font-mono text-[9px] text-white/60 tracking-widest mb-2" data-id="element-3032">
              EMPIEZA AQUÍ
            </div>
            <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-3033">
              ¿Hablamos?
            </div>
            <div className="text-sm opacity-80 leading-relaxed max-w-lg" data-id="element-3034">
              Cuéntanos qué necesitas. Presupuesto gratis y respuesta en menos
              de 24h.
            </div>
          </div>
          <div className="col-span-4 flex flex-col gap-2" data-id="element-3035">
            <button className="px-4 py-3 bg-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2" data-id="element-3036">
              Contactar ahora
              <ArrowRightIcon className="w-3 h-3" data-id="element-3037" />
            </button>
            <button className="px-4 py-3 border border-white/30 rounded-full text-xs font-medium inline-flex items-center justify-center gap-2" data-id="element-3038">
              <MessageCircleIcon className="w-3 h-3" strokeWidth={1.75} data-id="element-3039" />
              WhatsApp directo
            </button>
          </div>
        </div>
      </div>
    </BrowserFrame>;
}
/* ================================================================== */
/*  QR · SERVICIOS — PHONE                                             */
/* ================================================================== */
function QRServicesPhoneView() {
  return <PhoneFrame url="me-tro.co/q/servicios" data-id="element-3040">
      <div className="px-5 py-6" style={{
      background: '#0E0E12'
    }} data-id="element-3041">
        <div className="flex items-center justify-between mb-4" data-id="element-3042">
          <img src={METRO_LOGO_URL} alt="me—tro" className="h-3.5 w-auto" data-id="element-3043" />
          <div className="font-mono text-[7px] text-white/40 tracking-widest" data-id="element-3044">
            QR · /SERVICIOS
          </div>
        </div>
        <div className="font-mono text-[8px] text-[var(--accent-blue)] tracking-widest mb-2" data-id="element-3045">
          AGENCIA · BARCELONA
        </div>
        <div className="font-space font-bold text-[28px] leading-[0.95] tracking-tight text-white mb-3" data-id="element-3046">
          Tu negocio.
          <br data-id="element-3047" />
          <span className="text-[var(--accent-blue)]" data-id="element-3048">
            Con mejor cara online.
          </span>
        </div>
        <div className="font-helvetica text-[11px] text-white/70 leading-relaxed mb-4" data-id="element-3049">
          Web, redes, branding, foto y vídeo. Todo en un solo estudio.
        </div>
        <div className="flex flex-col gap-2" data-id="element-3050">
          <button className="px-3 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-3051">
            Ver qué hacemos
            <ArrowRightIcon className="w-3 h-3" data-id="element-3052" />
          </button>
          <button className="px-3 py-2.5 border border-white/30 text-white rounded-full text-[11px] font-medium" data-id="element-3053">
            Hablar ahora
          </button>
        </div>
      </div>
      <div className="px-5 py-5 bg-white" data-id="element-3054">
        <div className="font-mono text-[8px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-3055">
          QUÉ NECESITAS
        </div>
        <div className="font-space font-bold text-lg tracking-tight leading-tight mb-3" data-id="element-3056">
          Seis familias.
        </div>
        <div className="grid grid-cols-2 gap-2" data-id="element-3057">
          {[{
          l: 'Web',
          c: '#1A2BFF'
        }, {
          l: 'Redes',
          c: '#F26A35'
        }, {
          l: 'Branding',
          c: '#E8C547'
        }, {
          l: 'Foto · Vídeo',
          c: '#2A8E47'
        }, {
          l: 'Eventos',
          c: '#F4A523'
        }, {
          l: 'AI',
          c: '#0E0E12'
        }].map(s => <div key={s.l} className="border border-[var(--divider)] rounded-sm p-2.5" data-id="element-3058">
              <div className="w-2 h-2 rounded-sm mb-1.5" style={{
            background: s.c
          }} data-id="element-3059" />
              <div className="font-space font-bold text-[12px] leading-tight" data-id="element-3060">
                {s.l}
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-5 py-5" style={{
      background: '#1A2BFF'
    }} data-id="element-3061">
        <div className="font-mono text-[8px] text-white/75 tracking-widest mb-1.5" data-id="element-3062">
          OFERTA · WEB EXPRESS
        </div>
        <div className="font-space font-bold text-lg leading-tight tracking-tight text-white mb-1.5" data-id="element-3063">
          Web en 72h. €390.
        </div>
        <button className="w-full px-3 py-2 bg-white text-[var(--accent-blue)] rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5 mt-2" data-id="element-3064">
          Saber más
          <ArrowRightIcon className="w-3 h-3" data-id="element-3065" />
        </button>
      </div>
      <div className="px-5 py-5 bg-[var(--text-ink)] text-white" data-id="element-3066">
        <div className="font-space font-bold text-base leading-tight tracking-tight mb-1" data-id="element-3067">
          ¿Hablamos?
        </div>
        <div className="text-[10px] opacity-80 leading-relaxed mb-3" data-id="element-3068">
          Presupuesto gratis. Respuesta &lt; 24h.
        </div>
        <button className="w-full px-3 py-2.5 bg-[var(--accent-blue)] rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-3069">
          Contactar
          <ArrowRightIcon className="w-3 h-3" data-id="element-3070" />
        </button>
      </div>
    </PhoneFrame>;
}
/* ================================================================== */
/*  QR · SERVICIOS — WRAPPER                                           */
/* ================================================================== */
function QRServicesLandingMockup() {
  const [view, setView] = useState<'desktop' | 'mobile'>('desktop');
  return <div data-id="element-3071">
      <ViewportToggle view={view} onChange={setView} data-id="element-3072" />
      {view === 'desktop' ? <QRServicesDesktopView data-id="element-3073" /> : <QRServicesPhoneView data-id="element-3074" />}
    </div>;
}
/* ================================================================== */
/*  QR · WEB EXPRESS — DESKTOP                                         */
/* ================================================================== */
function QRWebExpressDesktopView() {
  return <BrowserFrame url="me-tro.co/q/web-express" data-id="element-3075">
      <div className="px-8 py-12" style={{
      background: '#1A2BFF'
    }} data-id="element-3076">
        <div className="grid grid-cols-12 gap-6 items-center" data-id="element-3077">
          <div className="col-span-7 text-white" data-id="element-3078">
            <div className="flex items-center justify-between mb-5" data-id="element-3079">
              <img src={METRO_LOGO_URL} alt="me—tro" className="h-4 w-auto invert brightness-200" data-id="element-3080" />
              <div className="text-[9px] font-bold tracking-widest bg-white/15 text-white px-2 py-0.5 rounded-full" data-id="element-3081">
                DESDE €390
              </div>
            </div>
            <div className="font-mono text-[9px] text-white/70 tracking-widest mb-3" data-id="element-3082">
              WEB EXPRESS · 72 HORAS
            </div>
            <div className="font-space font-bold text-6xl leading-[0.92] tracking-tight mb-4" data-id="element-3083">
              Tu web profesional,
              <br data-id="element-3084" />
              en menos de
              <br data-id="element-3085" />
              <span className="bg-white text-[var(--accent-blue)] px-2" data-id="element-3086">
                72 horas.
              </span>
            </div>
            <div className="font-helvetica text-sm opacity-85 leading-relaxed max-w-lg mb-6" data-id="element-3087">
              Diseño limpio. Hosting incluido el primer año. SEO básico. Sin
              letra pequeña, sin sorpresas.
            </div>
            <div className="flex items-center gap-3" data-id="element-3088">
              <button className="px-5 py-3 bg-white text-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center gap-2" data-id="element-3089">
                Quiero mi web ahora
                <ArrowRightIcon className="w-3 h-3" data-id="element-3090" />
              </button>
              <button className="px-5 py-3 border border-white/40 rounded-full text-xs font-medium" data-id="element-3091">
                Tengo dudas, hablar primero
              </button>
            </div>
            <div className="text-[10px] text-white/70 font-mono tracking-wider mt-4" data-id="element-3092">
              PRESUPUESTO GRATIS · RESPUESTA EN 24H · BARCELONA
            </div>
          </div>
          <div className="col-span-5" data-id="element-3093">
            <div className="bg-white/10 border border-white/20 rounded-sm p-5" data-id="element-3094">
              <div className="font-mono text-[8px] text-white/60 tracking-widest mb-3" data-id="element-3095">
                PROCESO · 4 PASOS
              </div>
              <div className="space-y-2" data-id="element-3096">
                {[['HOY', 'Nos cuentas el negocio'], ['24H', 'Propuesta y arranque'], ['48–72H', 'Diseño y desarrollo'], ['ENTREGA', 'Revisas, apruebas, online']].map(([n, t], i) => <div key={n} className="flex items-center gap-3 bg-white/10 rounded-sm p-2.5" data-id="element-3097">
                    <div className="font-mono text-[9px] font-bold tracking-widest w-14 flex-shrink-0 text-white" data-id="element-3098">
                      {n}
                    </div>
                    <div className="font-space font-bold text-[11px] leading-tight flex-1 text-white" data-id="element-3099">
                      {t}
                    </div>
                    <div className="font-mono text-[10px] text-white/60" data-id="element-3100">
                      0{i + 1}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-8 py-10 bg-white" data-id="element-3101">
        <div className="font-mono text-[9px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-3102">
          QUÉ INCLUYE · TODO POR €390
        </div>
        <div className="font-space font-bold text-3xl tracking-tight leading-tight mb-5" data-id="element-3103">
          Sin asteriscos.
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-id="element-3104">
          {[['Diseño personalizado', 'No plantilla. Adaptado a tu marca.'], ['Hasta 5 páginas', 'Inicio · Servicios · Sobre · Contacto · 1+'], ['Responsive móvil', '100% adaptada a cualquier dispositivo.'], ['Formulario contacto', 'Para que te escriban directamente.'], ['SEO básico', 'Google te encuentra desde el día uno.'], ['Google Analytics', 'Métricas de visitas y origen.'], ['30 días soporte', 'Ajustes y corrección de errores.'], ['Dominio guiado', 'Te ayudamos a registrarlo (~€80/año aparte).']].map(([t, d]) => <div key={t} className="flex items-start gap-3" data-id="element-3105">
              <CheckIcon className="w-4 h-4 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-3106" />
              <div data-id="element-3107">
                <div className="font-space font-bold text-sm leading-tight" data-id="element-3108">
                  {t}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] leading-snug mt-0.5" data-id="element-3109">
                  {d}
                </div>
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-8 py-10 grid grid-cols-12 gap-6" style={{
      background: '#EDEEF1'
    }} data-id="element-3110">
        <div className="col-span-6" data-id="element-3111">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-3112">
            PARA QUIÉN
          </div>
          <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-3113">
            ¿Es para ti?
          </div>
          <div className="space-y-2" data-id="element-3114">
            {['Tienes negocio local sin web (o con una de 2014)', 'Acabas de emprender · necesitas presencia ya', 'Presupuesto ajustado · no quieres calidad baja', 'Necesitas algo listo ya · no proyecto eterno'].map(t => <div key={t} className="flex items-start gap-2" data-id="element-3115">
                <CheckIcon className="w-3.5 h-3.5 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-3116" />
                <div className="text-[12px] leading-snug" data-id="element-3117">{t}</div>
              </div>)}
          </div>
        </div>
        <div className="col-span-6" data-id="element-3118">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-3119">
            FAQ
          </div>
          <div className="font-space font-bold text-2xl tracking-tight leading-tight mb-4" data-id="element-3120">
            Preguntas frecuentes.
          </div>
          <div className="space-y-2" data-id="element-3121">
            {['¿Las 72h son reales?', '¿Y si no me gusta el diseño?', '¿Puedo ampliar la web después?', '¿Hacéis tiendas online?'].map(q => <div key={q} className="bg-white rounded-sm border border-[var(--divider)] p-3 flex items-center justify-between" data-id="element-3122">
                <div className="font-space font-bold text-xs leading-tight" data-id="element-3123">
                  {q}
                </div>
                <PlusIcon className="w-3 h-3 text-[var(--text-muted)]" strokeWidth={1.5} data-id="element-3124" />
              </div>)}
          </div>
        </div>
      </div>
      <div className="px-8 py-12 bg-[var(--text-ink)] text-white" data-id="element-3125">
        <div className="grid grid-cols-12 gap-6 items-center" data-id="element-3126">
          <div className="col-span-8" data-id="element-3127">
            <div className="font-space font-bold text-4xl leading-[0.95] tracking-tight mb-3" data-id="element-3128">
              €390 · 72 horas · online.
            </div>
            <div className="text-sm text-white/70 leading-relaxed max-w-lg" data-id="element-3129">
              Sin complicaciones, sin sorpresas. Con un equipo que sabe lo que
              hace.
            </div>
          </div>
          <div className="col-span-4 flex flex-col gap-2" data-id="element-3130">
            <button className="px-4 py-3 bg-[var(--accent-blue)] rounded-full text-xs font-medium inline-flex items-center justify-center gap-2" data-id="element-3131">
              Quiero empezar
              <ArrowRightIcon className="w-3 h-3" data-id="element-3132" />
            </button>
            <button className="px-4 py-3 border border-white/30 rounded-full text-xs font-medium" data-id="element-3133">
              Hablar primero
            </button>
            <div className="text-[9px] text-white/50 text-center font-mono tracking-wider mt-1" data-id="element-3134">
              PRESUPUESTO GRATIS · 24H · BCN
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>;
}
/* ================================================================== */
/*  QR · WEB EXPRESS — PHONE                                           */
/* ================================================================== */
function QRWebExpressPhoneView() {
  return <PhoneFrame url="me-tro.co/q/web-express" data-id="element-3135">
      <div className="px-5 py-6" style={{
      background: '#1A2BFF'
    }} data-id="element-3136">
        <div className="flex items-center justify-between mb-4" data-id="element-3137">
          <img src={METRO_LOGO_URL} alt="me—tro" className="h-3.5 w-auto invert brightness-200" data-id="element-3138" />
          <div className="text-[8px] font-bold tracking-widest bg-white/15 text-white px-2 py-0.5 rounded-full" data-id="element-3139">
            DESDE €390
          </div>
        </div>
        <div className="font-mono text-[8px] text-white/70 tracking-widest mb-2" data-id="element-3140">
          WEB EXPRESS · 72 HORAS
        </div>
        <div className="font-space font-bold text-[26px] leading-[0.95] tracking-tight text-white mb-3" data-id="element-3141">
          Tu web profesional,
          <br data-id="element-3142" />
          en menos de
          <br data-id="element-3143" />
          <span className="bg-white text-[var(--accent-blue)] px-1.5" data-id="element-3144">
            72 horas.
          </span>
        </div>
        <div className="font-helvetica text-[11px] text-white/85 leading-relaxed mb-4" data-id="element-3145">
          Diseño limpio. Hosting incluido. SEO básico.
        </div>
        <button className="w-full px-3 py-2.5 bg-white text-[var(--accent-blue)] rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5" data-id="element-3146">
          Quiero mi web
          <ArrowRightIcon className="w-3 h-3" data-id="element-3147" />
        </button>
      </div>
      <div className="px-5 py-5 bg-white" data-id="element-3148">
        <div className="font-mono text-[8px] text-[var(--accent-blue)] tracking-widest mb-1.5" data-id="element-3149">
          QUÉ INCLUYE
        </div>
        <div className="font-space font-bold text-lg leading-tight tracking-tight mb-3" data-id="element-3150">
          Sin asteriscos.
        </div>
        <div className="space-y-2" data-id="element-3151">
          {[['Diseño personalizado'], ['Hasta 5 páginas'], ['Responsive móvil'], ['Formulario contacto'], ['SEO básico'], ['Google Analytics'], ['30 días soporte']].map(([t]) => <div key={t} className="flex items-start gap-2" data-id="element-3152">
              <CheckIcon className="w-3 h-3 text-[var(--accent-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.5} data-id="element-3153" />
              <div className="font-space font-bold text-[11px] leading-tight" data-id="element-3154">
                {t}
              </div>
            </div>)}
        </div>
      </div>
      <div className="px-5 py-6" style={{
      background: '#0E0E12'
    }} data-id="element-3155">
        <div className="font-space font-bold text-xl leading-tight tracking-tight text-white mb-1" data-id="element-3156">
          €390 · 72h · online.
        </div>
        <button className="w-full px-3 py-2.5 bg-[var(--accent-blue)] text-white rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5 mt-3" data-id="element-3157">
          Quiero empezar
          <ArrowRightIcon className="w-3 h-3" data-id="element-3158" />
        </button>
      </div>
    </PhoneFrame>;
}
/* ================================================================== */
/*  QR · WEB EXPRESS — WRAPPER                                         */
/* ================================================================== */
function QRWebExpressLandingMockup() {
  const [view, setView] = useState<'desktop' | 'mobile'>('desktop');
  return <div data-id="element-3159">
      <ViewportToggle view={view} onChange={setView} data-id="element-3160" />
      {view === 'desktop' ? <QRWebExpressDesktopView data-id="element-3161" /> : <QRWebExpressPhoneView data-id="element-3162" />}
    </div>;
}
export function WebsiteRedesignSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="website" ref={ref} className="min-h-screen px-8 py-24" data-id="element-3163">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-3164">
        <div className="mb-16 max-w-3xl" data-id="element-3165">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-3166">
            10 / WEBSITE REDESIGN
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-3167">
            The brand, online.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-3168">
            Las tres páginas principales del sitio reconstruidas en el nuevo
            sistema. Voz 2026 en castellano, paleta y tipografía actualizadas,
            CTAs nuevas, y la metáfora del metro deepened — sin "magia", sin
            "wow", sin exclamaciones forzadas. Pensado para Squarespace: solo
            bloques estándar (hero, columnas, accordion, formulario, footer)
            para que sea editable sin código.
          </p>
        </div>

        {/* Constraints strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-3169">
          {[{
          t: 'Idioma principal',
          v: 'Castellano',
          d: 'Inglés como secundario · CTAs en EN cuando funcionan mejor'
        }, {
          t: 'Sistema',
          v: 'Squarespace',
          d: 'Bloques estándar, sin custom code'
        }, {
          t: 'Paleta',
          v: 'Blue · Ink · Cool grey',
          d: 'Secundarios solo en gráficos de servicios'
        }, {
          t: 'Voz',
          v: '2026',
          d: 'Sin "magic" · sin "wow" · puntos en lugar de exclamaciones'
        }].map(c => <div key={c.t} className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)]" data-id="element-3170">
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-3171">
                {c.t.toUpperCase()}
              </div>
              <div className="font-space font-bold text-base leading-tight mb-1" data-id="element-3172">
                {c.v}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-3173">
                {c.d}
              </div>
            </div>)}
        </div>

        <div className="space-y-24" data-id="element-3174">
          {/* 10.1 — Home */}
          <PageRedesignBlock number="10.1" title="Inicio" url="metroagency.co" intro="La home pasa del crema con mapa de metro a un sistema más sobrio: gris frío como fondo, mapa traducido a red de servicios (estrategia / creatividad / producción / performance), y la nueva tagline en castellano como pieza central." notes={[{
          tag: 'HERO',
          title: '"Donde las ideas conectan."',
          body: 'La tagline 2026 sustituye al texto largo anterior. Eyebrow corto en azul, párrafo de apoyo en gris para que el titular respire.'
        }, {
          tag: 'CTA',
          title: '"Build something that moves people"',
          body: 'CTA hero — la única que mantiene inglés porque tiene doble lectura (mover personas + tránsito). El secundario en castellano: "Ver trabajos".'
        }, {
          tag: 'VISUAL',
          title: 'Red de servicios, no mapa decorativo',
          body: 'Las cuatro líneas representan estrategia, creatividad, producción y performance. Las estaciones son donde se cruzan.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque azul a sangre',
          body: 'Las 80 palabras del manifiesto se introducen aquí en versión corta. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'CIERRE',
          title: '"Start the conversation"',
          body: 'CTA final más blanda que la del hero. Sin formularios largos en home — solo invitación.'
        }]} mockup={<HomePageMockup data-id="element-3176" />} data-id="element-3175" />

          {/* 10.2 — Services */}
          <PageRedesignBlock number="10.2" title="Servicios" url="metroagency.co/servicios" intro="El hero azul se mantiene como en la versión actual (era lo más fuerte del sitio), pero la copy cambia a la metáfora de línea/estaciones. Los tres bloques actuales se reordenan en cuatro estaciones de servicio y los accordion se convierten en cards expandibles con checklist." notes={[{
          tag: 'HERO',
          title: '"Una línea. Cuatro estaciones."',
          body: 'Mantiene el azul a sangre del actual pero la copy se ajusta al sistema verbal nuevo. Eyebrow + headline + subhead corto.'
        }, {
          tag: 'ESTRUCTURA',
          title: 'De 3 accordion a 3 cards con checklist',
          body: 'Squarespace no permite UX complejo en accordion, así que cada categoría es una card con número grande + título + sublistado siempre visible.'
        }, {
          tag: 'CATEGORÍAS',
          title: 'Creatividad · Marketing & Performance · Producción',
          body: 'Mismas tres familias que el sitio actual, pero renombradas para alinear con el manifesto. Cada una con 4 bullets concretos.'
        }, {
          tag: 'APPROACH',
          title: 'Bloque "Cómo trabajamos"',
          body: 'Tres cards horizontales con los diferenciadores: un equipo, un contacto, un lenguaje. Refuerza la propuesta end-to-end sin usar la palabra "integral".'
        }, {
          tag: 'CTA',
          title: '"Map your next campaign"',
          body: 'El CTA del set 2026 más relevante para esta página — sugiere planificación, no venta. Termina la página sin presión.'
        }]} mockup={<ServicesPageMockup data-id="element-3178" />} data-id="element-3177" />

          {/* 10.3 — About */}
          <PageRedesignBlock number="10.3" title="Nosotros" url="metroagency.co/nosotros" intro="La página About reemplaza el círculo decorativo del logo con un manifiesto a sangre en negro. Recupera la historia de fundación (Barcelona, dificultad para encontrar una agencia end-to-end) y la combina con el sistema verbal nuevo, la matriz de equipo y los números." notes={[{
          tag: 'HERO',
          title: '"Construimos la infraestructura."',
          body: 'Reemplaza "Who are we" por una afirmación de qué hacemos, no de qué somos. La historia de fundación pasa a párrafo de apoyo.'
        }, {
          tag: 'MANIFESTO',
          title: 'Bloque negro a sangre',
          body: 'Las 80 palabras completas aparecen aquí — es la página donde el manifesto vive en su forma larga. Cierre con el lema "Tus ideas · Nuestras vías · Movamos".'
        }, {
          tag: 'VOZ',
          title: 'Tabla usamos / nunca',
          body: 'Bloque visual con las palabras que sí y no usamos. Hace que la política de voz sea pública — útil para clientes y para colaboradores externos.'
        }, {
          tag: 'EQUIPO',
          title: 'Grid 4 columnas',
          body: 'Foto + nombre + rol. Sin bios largas. Squarespace lo permite con bloque image grid estándar.'
        }, {
          tag: 'NÚMEROS',
          title: '"Estaciones por las que hemos pasado"',
          body: 'Reframe de los típicos números de agencia (campañas, marcas, sectores, idiomas) bajo la metáfora de tránsito.'
        }, {
          tag: 'CTA',
          title: '"Let\u2019s connect the dots"',
          body: 'CTA azul a sangre. La invitación más suave del set — para una página que cierra contando quiénes somos, no vendiendo nada.'
        }]} mockup={<AboutPageMockup data-id="element-3180" />} data-id="element-3179" />

          {/* 10.4 — Packs */}
          <PageRedesignBlock number="10.4" title="Packs" url="metroagency.co/packs" intro="Página de packs conectada desde Servicios — cuatro tiers (Micro, Essentials, Professional, Premium) más una sección de add-ons. Precios con IVA incluido para transparencia. Tier 0 deliberadamente limitado como puerta de entrada; cada pack tiene CTA directo de compra y opción de agendar llamada de 15 min." notes={[{
          tag: 'HERO',
          title: 'Cuatro tiers visibles arriba',
          body: 'Strip con los 4 tiers (Micro / Essentials / Professional / Premium) inmediatamente debajo del titular — orientación rápida antes de hacer scroll.'
        }, {
          tag: 'TIER 0',
          title: 'Micro tratado como puerta de entrada',
          body: 'Se presenta solo, con limitaciones explícitas en naranja. Filosofía clara: pack barato para eliminar la objeción de precio, con fricción que empuja al upgrade.'
        }, {
          tag: 'PRECIO',
          title: 'IVA incluido siempre visible',
          body: 'Cada precio lleva la etiqueta "IVA INCL." en mono. Sin asteriscos, sin sorpresas. La pregunta sobre IVA está la primera del FAQ.'
        }, {
          tag: 'COMBOS',
          title: 'Badge "COMBO" con ahorro real',
          body: 'Brand + Web Combo y Full System llevan badge y línea de ahorro. El ahorro está en euros, no en porcentaje vago.'
        }, {
          tag: 'ADD-ONS',
          title: 'Tabla negra con etiqueta "RECURRENTE"',
          body: 'Add-ons mensuales (SEO, social, mantenimiento) van marcados con badge "RECURRENTE" para que el cliente no se sorprenda con cuota.'
        }, {
          tag: 'CTA STICKY',
          title: 'Bloque negro con "Agendar llamada · 15 min"',
          body: 'Sidebar fija en la sección FAQ. Para quien todavía no sabe qué pack comprar — empuja a conversación corta, no a formulario largo.'
        }]} mockup={<PacksPageMockup data-id="element-3182" />} data-id="element-3181" />

          {/* 10.5 — Terms */}
          <PageRedesignBlock number="10.5" title="Términos y condiciones" url="metroagency.co/terminos" intro="Página legal con 15 secciones desde datos de la empresa hasta jurisdicción aplicable. Contenido con placeholders [ Texto pendiente ] mientras se valida con asesoría legal. Layout de índice navegable + contenido en columna estándar — bloque Squarespace de dos columnas con índice anchor." notes={[{
          tag: 'ESTRUCTURA',
          title: '15 secciones numeradas',
          body: 'De "Sobre Metro Agency" a "Jurisdicción". Incluye lo que el usuario pidió: devoluciones (06), garantía post-entrega (07), condiciones de pago (03), cancelaciones (10), más los estándar (RGPD, IP, jurisdicción).'
        }, {
          tag: 'ÍNDICE',
          title: 'TOC fijo a la izquierda',
          body: 'Anchors clicables. Sección actual marcada en azul. Mantiene la página navegable aunque sea larga.'
        }, {
          tag: 'PLACEHOLDERS',
          title: '[ Texto pendiente ] visible',
          body: 'Cada sección que necesita validación de asesoría aparece marcada explícitamente. Las secciones legalmente referenciables (Ley 37/1992, RD 1/2007, RGPD) ya citan la normativa.'
        }, {
          tag: 'ACEPTACIÓN',
          title: 'Bloque azul al cierre',
          body: 'Recuerda que contratar = aceptar, e invita a escribir a legal@metroagency.co antes de firmar si algo no encaja. Tono Metro, no tono abogado.'
        }, {
          tag: 'METADATA',
          title: 'Última actualización + versión',
          body: 'Fecha y versión visibles. Estándar legal cuando los términos cambian con el tiempo.'
        }]} mockup={<TermsPageMockup data-id="element-3184" />} data-id="element-3183" />

          {/* 10.6 — AI Solutions */}
          <PageRedesignBlock number="10.6" title="AI Solutions" url="metroagency.co/ai" intro="Nueva línea de servicio dedicada a IA, conectada desde Packs y desde Servicios. Cinco familias (Chatbot, Workflow, Agents, Consulting, Tools) con precio en formato setup + mensualidad. Hero oscuro para diferenciarla de Packs (claro) — la IA es la cara nueva del estudio, no la repetición de la oferta clásica. Cierra con un bloque de upsells que conecta cada servicio Metro existente con su upgrade IA natural." notes={[{
          tag: 'HERO',
          title: '"Tu negocio, respondiendo a las 2am."',
          body: 'Hero oscuro con copy concreta — habla del problema, no de la tecnología. CTA primario lleva al Audit (€490) porque es la puerta de entrada baja-fricción.'
        }, {
          tag: 'ESTRUCTURA',
          title: '5 familias · precios setup + mensual',
          body: 'Chatbot (3 tiers) · Workflow (3 tiers) · Agents (rango) · Consulting (Audit + Retainer) · Tools (GPTs + Setups). Cada card muestra setup y mensual lado a lado — es la economía real de la IA, no se esconde.'
        }, {
          tag: 'AGENTS',
          title: 'Rango de precio + casos tópicos',
          body: 'Para AI Agents no usamos tier porque cada proyecto es custom. Mostramos rango (€3.500–€8.000) y los 4 casos de uso típicos al lado: lead qual, post-venta, reporting, outreach.'
        }, {
          tag: 'UPSELL',
          title: 'Bloque oscuro · conexiones naturales',
          body: 'Tabla "Tienes X · Pregunta · Añades Y" para los upsells que el cliente entiende: Web → Chatbot, Social → DMs bot, Brand → Custom GPT, Cualquiera → Audit. 10% descuento si ya es cliente.'
        }, {
          tag: 'CTA',
          title: '"Empieza por el Audit"',
          body: 'Sticky azul en FAQ. €490, baja fricción, sin compromiso. La invitación es informarse, no contratar el flagship.'
        }, {
          tag: 'CONFIANZA',
          title: 'Datos en UE · RGPD · sin permanencia',
          body: 'Footer de la CTA repite las tres preocupaciones reales del cliente con IA: datos, regulación, lock-in. Sin esto, el resto no cierra.'
        }]} mockup={<AIPageMockup data-id="element-3186" />} data-id="element-3185" />

          {/* 10.7 — Contacto */}
          <PageRedesignBlock number="10.7" title="Contacto" url="metroagency.co/contacto" intro="Página de contacto pensada como canal de entrada bajo en fricción — no como muro de formulario corporativo. Layout dividido: form a la izquierda (un solo paso, dropdown de servicio para enrutar internamente), canales directos a la derecha (WhatsApp, email, Instagram, estudio físico). Hero oscuro con tono Metro: invitación, no captura." notes={[{
          tag: 'HERO',
          title: '"Hablemos. Sin formulario eterno."',
          body: 'Hero oscuro, sin imagen — la promesa está en el copy. Tres strips de confianza (24h, gratis, sin compromiso) en mono debajo del subtítulo.'
        }, {
          tag: 'FORM',
          title: 'Un solo paso · dropdown de enrutamiento',
          body: 'Campos comunes en grid de 2, el dropdown "¿En qué podemos ayudarte?" abre pills con todas las familias incluida AI. Se conecta al CRM interno para rutear según servicio.'
        }, {
          tag: 'CANALES',
          title: 'Sidebar con 4 canales directos',
          body: 'WhatsApp, email, Instagram y estudio físico — cada uno con micro-promesa concreta (< 2h, DMs abiertas, con cita previa). Para quien no quiere rellenar nada.'
        }, {
          tag: 'TRUST',
          title: 'Tres números reales',
          body: '+120 proyectos, 24h respuesta, 0 permanencia. Tres cifras que importan, no las típicas de agencia ("años de experiencia").'
        }, {
          tag: 'FAQ',
          title: 'FAQ corta · 4 preguntas',
          body: 'Solo las dudas que llegan antes de escribir: precio, tamaño, contrato, compromiso. Para que el formulario sea el siguiente paso, no la barrera.'
        }]} mockup={<ContactPageMockup data-id="element-3188" />} data-id="element-3187" />

          {/* 10.8 — Solicitar */}
          <PageRedesignBlock number="10.8" title="Solicitar presupuesto" url="metroagency.co/solicitar/web" intro="5 formularios específicos por familia (Web, Redes, Branding, Foto·Vídeo, Eventos) accesibles por tabs sticky. Cada uno tiene un bloque común (datos, plazo, presupuesto orientativo) más campos específicos del servicio. Esta página es la que se enlaza desde Servicios, Packs y AI — cliente que ya sabe qué quiere viene directo aquí, no a /contacto." notes={[{
          tag: 'TABS',
          title: 'Sticky tabs por familia',
          body: 'Web · Redes · Branding · Foto·Vídeo · Evento. Se quedan fijos al hacer scroll para que el cliente pueda cambiar de formulario sin perder el sitio. URL canónica por tab (/solicitar/web, /solicitar/redes...).'
        }, {
          tag: 'COMUNES',
          title: 'Bloque "datos generales" reutilizable',
          body: 'Nombre, email, teléfono, plazo y presupuesto orientativo aparecen iguales en los 5 formularios — en gris claro arriba. El resto del form es específico del servicio.'
        }, {
          tag: 'PILLS',
          title: 'Selección con pills, no checkboxes',
          body: 'Tipo de web, contenidos, redes, extras... todo en pills tocables (móvil-friendly). Marcado = relleno color marca; no marcado = borde discreto. Más rápido que checkboxes en touch.'
        }, {
          tag: 'SIDEBAR',
          title: 'Promesa + lista de otros forms',
          body: 'Sidebar derecho: bloque negro "presupuesto cerrado en 24h" + lista de los otros formularios + un fallback al form general /contacto para quien no sabe qué pedir.'
        }, {
          tag: 'CTA',
          title: 'CTA específico por servicio',
          body: '"Solicitar presupuesto de web" / "...de branding" — el botón siempre dice qué se está pidiendo. Reduce ambigüedad y mejora analítica de conversión.'
        }]} mockup={<RequestFormsPageMockup data-id="element-3190" />} data-id="element-3189" />

          {/* 10.9 — Portfolio */}
          <PageRedesignBlock number="10.9" title="Portfolio" url="metroagency.co/portfolio" intro="Vitrina de trabajos con filtros sticky por categoría y grid asimétrico (proyectos destacados ocupan más espacio). El protagonismo es la imagen — texto mínimo hasta el hover. Sirve como prueba para nuevos clientes, referencia para redes y catálogo interno para el equipo comercial." notes={[{
          tag: 'HERO',
          title: '"El trabajo habla solo."',
          body: 'Hero oscuro con headline doble (segunda línea en gris para crear pausa). Strip de 4 stats (36 proyectos, 7 categorías, 18 sectores, desde 2019) bajo el subtítulo — credibilidad sin auto-bombo.'
        }, {
          tag: 'FILTROS',
          title: 'Pills sticky con contador',
          body: 'Filtros en pills horizontales (Todos · Web · Redes · Branding · Foto · Vídeo · Eventos) con número de proyectos en cada uno. Sticky al hacer scroll. Pill activa en negro a sangre.'
        }, {
          tag: 'GRID',
          title: 'Asimétrico · cards 4:3',
          body: 'Grid de 12 columnas con cards que ocupan 4/6/8 cols según importancia. Cada card lleva badge de color por categoría (azul, naranja, verde, amarillo) y año. Hover revela "Ver proyecto →".'
        }, {
          tag: 'COLOR',
          title: 'Backgrounds variados, no foto-only',
          body: 'Cards alternan fondo cool grey, ink negro, azul marca y secundarios. Mantiene la página viva sin obligar a tener foto profesional de cada proyecto.'
        }, {
          tag: 'CTA',
          title: 'Bloque azul · "podemos hacer algo así"',
          body: 'Cierre de página con CTA grande en azul: "¿Ves algo que te gusta? Podemos hacer algo así para ti. O mejor." + botón a /solicitar.'
        }]} mockup={<PortfolioPageMockup data-id="element-3192" />} data-id="element-3191" />

          {/* 10.10 — QR landing servicios */}
          <PageRedesignBlock number="10.10" title="QR Landing · Servicios" url="me-tro.co/q/servicios" intro="Landing mobile-only para QRs de poster general (los 3 generales del set 11). Dominio corto me-tro.co/q/* para minimizar errores de captura. Carga instantánea, sin nav, sin pop-ups. Responde en 5 segundos: quiénes somos, qué hacemos, qué hago ahora. Reemplaza al sitio principal cuando el origen es la calle." notes={[{
          tag: 'FRAME',
          title: 'Phone frame, no browser',
          body: 'El mockup se renderiza dentro de un PhoneFrame para subrayar que es 100% mobile. Sin nav superior — el espacio se gana para el contenido.'
        }, {
          tag: 'HERO',
          title: 'Above-the-fold completo',
          body: '"Tu negocio. Con mejor cara online." + dos CTAs (azul primario · borde secundario). Visible sin scroll en cualquier móvil moderno. Logo me—tro pequeño + etiqueta "QR · /SERVICIOS" en mono.'
        }, {
          tag: 'SERVICIOS',
          title: 'Grid 2×3 de familias',
          body: 'Las 5 familias (Web, Redes, Branding, Foto·Vídeo, Eventos) + AI como bloque destacado. Cada card con barra de color y descripción de una línea. Toque entero clicable.'
        }, {
          tag: 'OFERTA',
          title: 'Bloque azul a sangre · €390/72h',
          body: 'Antes del cierre, gancho conversión: Web Express por €390 en 72h. Solo CTA "Saber más" — no se vende todo aquí, se enlaza a /q/web-express.'
        }, {
          tag: 'CTA',
          title: 'Cierre con WhatsApp directo',
          body: 'Footer negro con CTA azul "Contactar ahora" + texto alterno "O por WhatsApp" con icono. Para visitante de calle, WhatsApp suele ganar al formulario.'
        }]} mockup={<QRServicesLandingMockup data-id="element-3194" />} data-id="element-3193" />

          {/* 10.11 — QR landing web express */}
          <PageRedesignBlock number="10.11" title="QR Landing · Web Express" url="me-tro.co/q/web-express" intro="Landing dedicada para los QRs de los pósters de Web Design (€390 en 72h). Pieza comercial de mayor valor a corto plazo, por lo que necesita resolver objeciones (precio, tiempo, alcance, qué pasa con dominio/hosting) y empujar a contacto sin fricción. Phone frame también — todo el tráfico de QR es móvil." notes={[{
          tag: 'HERO',
          title: 'Hero azul a sangre + badge €390',
          body: 'Headline grande con "72 horas" resaltado en blanco sobre azul invertido. Badge "DESDE €390" arriba a la derecha — el precio nunca se esconde. CTA blanco contrastado.'
        }, {
          tag: 'INCLUYE',
          title: 'Checklist de 7 puntos · sin asteriscos',
          body: 'Lista completa de lo que cubre el pack (diseño custom, 5 páginas, responsive, formulario, SEO básico, Analytics, 30 días soporte). Aviso explícito de dominio/hosting aparte en bloque gris al final.'
        }, {
          tag: 'PROCESO',
          title: 'Cronograma de 4 pasos',
          body: 'HOY → 24h → 48–72h → ENTREGA. Cards con tag mono + descripción corta + número de paso. Hace la promesa de las 72h tangible.'
        }, {
          tag: 'OBJECIONES',
          title: 'Bloque "¿es para ti?" + FAQ teaser',
          body: 'Identificación del perfil (negocio local sin web, recién emprendido, presupuesto ajustado) seguido de las 4 preguntas más bloqueantes en colapso. Para resolver dudas sin scroll infinito.'
        }, {
          tag: 'CTA',
          title: 'Cierre negro con doble CTA',
          body: '"€390 · 72 horas · online." Headline rotundo, dos CTAs (azul "Quiero empezar" + borde "Prefiero hablar") y micro-confianza "PRESUPUESTO GRATIS · 24H · BCN".'
        }]} mockup={<QRWebExpressLandingMockup data-id="element-3196" />} data-id="element-3195" />
        </div>
        {/* Sitemap notes */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3197">
          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-3198">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-3199">
              NAVEGACIÓN
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-3200">
              Toda la línea, sin trasbordo.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-3201">
              {[['Inicio', 'Punto de entrada. Manifiesto corto + red de servicios + selected work.'], ['Servicios', 'Cuatro estaciones. Listado completo + cómo trabajamos.'], ['Packs', 'Cinco categorías (Web, Brand, Content, Marketing, 360°) + add-ons.'], ['AI', 'Cinco familias de IA · setup + mensual. Auditoría como puerta de entrada.'], ['Portfolio', 'Grid filtrable por categoría. 36 proyectos, 7 sectores.'], ['Nosotros', 'Manifiesto largo + equipo + números.'], ['Contacto', 'Form general · canales directos · FAQ corta.'], ['Solicitar', '5 formularios específicos por servicio. CRM-ready.'], ['Términos', 'Página legal con 15 secciones. Link en footer.'], ['QR / Express', 'Landings mobile para QRs de pósters. Dominio corto me-tro.co/q/*.']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-3202">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-3203">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-3204">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="border border-[var(--divider)] rounded-sm p-6 bg-[var(--bg-surface)]" data-id="element-3205">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-3" data-id="element-3206">
              CONSTRUCCIÓN · SQUARESPACE
            </div>
            <div className="font-space font-bold text-xl mb-4 tracking-tight" data-id="element-3207">
              Solo bloques estándar.
            </div>
            <div className="space-y-2 text-sm font-helvetica" data-id="element-3208">
              {[['Hero', 'Section con background color + título + texto + button'], ['Servicios', 'Cards section con 3-4 columnas + icon block'], ['Manifesto', 'Section con background ink/blue + texto a sangre'], ['Trabajos', 'Image gallery grid + link'], ['Equipo', 'People block o image grid 4 cols'], ['Footer', 'Footer estándar Squarespace con 4 columnas']].map(([k, v]) => <div key={k} className="flex items-start gap-3 py-1" data-id="element-3209">
                  <span className="font-mono text-xs text-[var(--accent-blue)] font-bold w-20 flex-shrink-0" data-id="element-3210">
                    {k}
                  </span>
                  <span className="text-[var(--text-muted)] text-xs leading-relaxed" data-id="element-3211">
                    {v}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}